import * as PrefabManager from '../PrefabManager';
import { BaseVehicle } from './BaseVehicle';
import './NoVehicle';
import { Humanoid } from '../humanoids/Humanoid';
import { game } from '../../Game';
import { Settings } from '../../Settings';
import { rayCastPortalAware } from '../guns/portalPhysicsQuery';
import { endpointUsesHostFixture, pointWithinPortalAperture } from '../guns/portalCollision';
import { portalNormal } from '../guns/portalMath';
import { simulationFrame } from '../../utils/SimulationClock';
import * as AudioManager from '../../utils/AudioManager';
import * as MobileController from '../../utils/MobileController';

const vec1 = new Box2D.b2Vec2();
const vec2 = new Box2D.b2Vec2();
const heldManifold = new Box2D.b2WorldManifold();

// The runner is the character itself. A spring along a ground ray carries it at hip height and every
// part gets the same velocity change, so steering never strains a joint. The limbs only animate:
// legs by two-bone IK to feet planted on the ground, arms and head by joint motors.
// Speeds are m/s and accelerations m/s² of physics time; times are ms.
const MOVE = {walk: 4, run: 14, crouch: 2.5, runAfter: 250, runRamp: 700, accel: 40, brake: 60, skidBrake: 30, crouchBrake: 8, overBrake: 12, turnBrake: 90, airSpeed: 7, airAccel: 24, airDrag: 6, wallReach: 1.1, wallFacing: 0.7};
const HOVER = {stiffness: 140, damping: 22, snap: 0.9, step: 1, maxAccel: 80, maxRise: 6, slope: 0.6, probe: 0.9, edge: 0.4, straddle: 0.8, stick: 0.15, through: 0.02, sameSlope: 0.98};
// A foot stands with ground under its heel and toe, half a foot either way of its middle; off an edge it's moved over in steps
// of step m, up to reach m, onto the nearest ground.
const FOOTING = {half: 0.15, step: 0.05, reach: 1.2};
// Gravity is scaled by the jump's state: heavier once jump is let go or the runner falls, lighter around the top while it's held.
const JUMP = {speed: 15.5, doubleSpeed: 14, buffer: 150, coyote: 120, groundIgnore: 160, reland: 1, flipTime: 520,
    riseGravity: 2, cutGravity: 10, apexGravity: 1, apexSpeed: 2.5, fallGravity: 1.8, maxFall: 22};
// Ledges are found from the shoulders: a top within reach above them, with its edge a little ahead. While hanging the
// controller carries the body and the arms only pose, so they never hold its weight. Distances are from the ledge's edge.
const LEDGE = {minWidth: 1, minMass: 0.5, reach: 3, below: 4.5, vaultSpeed: 3, vaultFall: 8, drop: 700, probes: [0.5, 0.9, 1.3, 1.7, 2.1, 2.5], maxRise: 10, after: 100, cooldown: 350, back: 1.6, minBack: 0.9, headGap: 0.2, depth: 3.6, highDepth: 0.8, grip: {left: -0.1, right: -0.2}, gripAbove: 0.15, curl: 55, elbow: 25, lip: [1.6, 2.4], overhead: 115, catchBand: 0.9,
    gain: 10, maxSpeed: 14, settle: 6, settleRate: 1.5, settleReach: 0.9, accel: 150, lean: 0, strain: 0.15, tear: 0.2, tearFrames: 18, raised: 100, reachArm: 70, handReach: 0.5, gripFrames: 10};
const CLIMB = {time: 650, split: 0.55, vaultTime: 380, vaultSplit: 0.3, over: 1.6, back: 1.3, onto: 1.5, lean: 0.35, abort: 2.5};
const LEAP = {away: 9, up: 11};
const WALL = {raise: 4.5, reach: 2.5, standOff: 1.5, settle: 0.6, backOff: 4, air: 1.6, flip: 3.5, lookAhead: 0.5, brake: 60, arrive: 0.8};
// Something loose taller than a step is pushed, not run into: the hips are held contact m off it, looked for reach m ahead and
// clear m above the step, and within touch m of that it's shoved with force m/s² of the runner's mass, up to speed m/s for
// something as heavy as the runner or heavier and faster the lighter it is.
const PUSH = {speed: 3, contact: 1, touch: 0.3, force: 30, reach: 6, clear: 0.05};
// Falling (faster than start m/s, so the air jump at the top of a jump still works) beside a wall while steering into it
// slides down it at speed, hands and a foot on it, held standOff off it so the knees stay clear. Positions are from the
// hips and shoulders along the wall.
const SLIDE = {catch: 2.2, keep: 2.8, start: 4, speed: 5, brake: 45, slip: 8, standOff: 1.8, gain: 8, accel: 60, foot: 3.4, footGap: 0.55, backFoot: {x: -0.9, y: 3.2},
    hands: {left: 2.3, right: 1.2}, handGap: 0.35, lean: 0.1, tearFrames: 12};
// Jumping off a wall kicks away from it, also for grace ms after leaving it or with one at the back. Steering is ignored for
// lock ms afterwards, so holding towards the wall doesn't cancel the kick. Kicking off the face last kicked off again
// (normals alike past same, within plane m of it) only works lower down than before, until the runner lands.
const KICK = {away: 9, up: 14, reach: 2.5, grace: 150, lock: 200, same: 0.98, plane: 0.5};
// A linked portal within reach m is an opening in what it's on: rays pass through where they meet that surface (within
// plane m of the portal's face) inside the opening, and the portal's own rim and backstop aren't walls.
const PORTAL = {reach: 20, plane: 0.1};
// Holding down while falling, the roll starts in the air once the ground is lead ms away (looked for up to lookAhead m
// below), so the body is already turning as it lands; its turn waits at airHold until the body is within contact m of the
// ground. Landing with down pressed just before rolls too, and so does pressing down while running: first the legs take
// the landing over absorb ms, sinking into a crouch (braking the fall at up to catch m/s²) while the body tips forward
// pitch rad, and carry share of the fall on as roll speed. Then the hands reach for the ground first (the first hands share of the roll), then the body balls up tight, knees to the chest, and turns a full
// turn forwards along the ground in the first turnTime share of it, held clear m over it. From plant the feet swing in under the hips while it's still
// turning, standing on the ground from its end, and over the last share (rise) the body comes up into a crouch (share of
// the leg's reach) that runs on. A rolled landing survives drops up to maxDrop m; an unrolled one crashes falling faster
// than RAGDOLL.fall.
const ROLL = {lead: 160, lookAhead: 40, contact: 0.3, airHold: 0.24, absorb: 110, catch: 220, pitch: 0.55, carry: 0.4, time: 650, turnTime: 0.82, clear: 0.1, hands: 0.18, plant: [0.5, 0.74], rise: 0.6, crouch: 0.72, speed: 8, accel: 40, minFall: 6, maxDrop: 30, dive: 7,
    buffer: 250, probe: 9, wall: 2.5, jumpAfter: 0.6, head: 0.9,
    reach: {left: [115, 15], right: [100, 20]}, out: {left: [60, 45], right: [45, 55]},
    tuck: {front: {x: 0.7, y: 0.6}, back: {x: 0.45, y: 0.8}, arms: {left: [75, 135], right: [65, 135]}},
    under: {front: {x: 0.6, y: 2.4}, back: {x: -0.3, y: 2.6}}};
const WEDGE = {frames: 6, push: 4, slip: 10, rejoin: 0.1};
const CAUGHT = [['hand_left', 'arm_left', 'shoulder_left'], ['hand_right', 'arm_right', 'shoulder_right'], ['feet_left', 'leg_left', 'thigh_left'], ['feet_right', 'leg_right', 'thigh_right']];
// The body turns at a rate proportional to how far it is off, closing part of the gap to that rate each step.
const UPRIGHT = {rate: 8, airRate: 5, flipRate: 14, response: 0.35, maxSpin: 9, stand: 1.05};
// Cycle is the distance of a full stride (two steps), duty the share of it a foot stands.
const GAIT = {walkCycle: 5.2, runCycle: 11, walkDuty: 0.62, runDuty: 0.36, stance: 0.35, idleSpeed: 1.5, smoothing: 0.25, settle: 150};
// A foot's way through the air from lifting off to setting down, walking and running: keys of [ahead of the hip as a
// share of the stride, height as a share of the leg's reach, toe pitch]. Running, the heel kicks up behind, the knee
// drives through high and the foot reaches out and paws back under the body.
const SWING = {
    walk: [[-0.5, 0, -0.35], [-0.32, 0.09, -0.45], [0.02, 0.12, -0.1], [0.38, 0.07, 0.2], [0.5, 0, 0.1]],
    run: [[-0.5, 0, -0.55], [-0.62, 0.36, -0.75], [-0.05, 0.46, -0.35], [0.62, 0.22, 0.05], [0.5, 0, -0.1]],
};
// The body rises and falls twice a stride (shares of the leg's reach): walking it vaults over the standing leg, running it
// sinks onto it and flies between steps. It leans into acceleration (rad per m/s², smoothed over accelTime ms) and breathes
// standing still.
const BODY = {walkBob: 0.035, runBob: 0.05, bobPitch: 0.03, accelLean: 0.008, maxAccelLean: 0.3, accelTime: 90,
    breathe: 3600, breath: 0.012, breathLean: 0.025};
// Arms swing against the legs, forward with the other leg; shoulder centre and swing, elbow bend and how much more it
// bends forward (degrees). Hands follow loosely. Reaching, an arm still fold degrees from where it's going keeps its
// elbow folded to folded.
// Braking hard the runner skids: lead foot planted out front (shares of the leg's reach ahead of the hips), sunk a little
// lower, arms thrown forward. It sets in over attack ms and lets go over release ms.
const SKID = {attack: 90, minSpeed: 3, lead: 0.5, trail: -0.12, sink: 0.07, arms: {left: [75, 35], right: [55, 45]}, release: 250};
const ARMS = {walk: {shoulder: 6, swing: 20, elbow: 14, bend: 12}, run: {shoulder: 28, swing: 44, elbow: 84, bend: 18}, idle: {shoulder: 4, elbow: 12},
    crouch: {shoulder: 40, elbow: 75}, hand: 0.7, fold: [25, 60], folded: 130};
// The head keeps its own level while the body bobs and leans, looking down a little (rad) running.
const HEAD = {stand: 0, run: 0.12, crouch: 0.2, wall: 0.25, air: 0.05, fall: 0.35, tuck: 0.35};
// Falling fast for a while the runner panics: arms flap overhead out of step and the legs pedal, until the ground is
// ground ms away. Flapping sets in after ms of falling faster than speed m/s, fully ramp ms later.
const FLAIL = {speed: 10, after: 150, ramp: 250, period: 380, ground: 150, probe: 60, shoulder: [70, 165], elbow: [20, 75], skew: 0.35,
    front: {x: 1.3, y: 2.3}, back: {x: -1, y: 3.5}};
// Heights are shares of the leg's reach; lean is radians forward.
const POSE = {stand: 0.98, run: 0.88, crouch: 0.5, crouchLean: 0.3, lean: 0.06, runLean: 0.24, landTime: 260, land: 0.3};
const AIR = {
    rise: {front: {x: 0.9, y: 2.5}, back: {x: -0.7, y: 3.0}, arms: {left: [140, 30], right: [120, 40]}},
    fall: {front: {x: 0.5, y: 3.4}, back: {x: -0.3, y: 3.5}, arms: {left: [95, 20], right: [80, 25]}},
    // A running jump is a stride: lead knee up, trailing leg out behind, arms opposed. It reaches for the ground coming down.
    leap: {front: {x: 1.9, y: 2.1}, back: {x: -2.1, y: 2.7}, arms: {left: [10, 40], right: [150, 30]}},
    leapFall: {front: {x: 1.4, y: 3.1}, back: {x: -1.1, y: 3.2}, arms: {left: [40, 30], right: [120, 30]}},
    tuck: {front: {x: 0.7, y: 1.8}, back: {x: 0.5, y: 2.0}, arms: {left: [60, 110], right: [55, 110]}},
    roll: {front: {x: 1.1, y: 2.5}, back: {x: 0.4, y: 2.9}, arms: {left: [100, 25], right: [85, 30]}},
    hop: {x: 0.35, y: 3.1},
    hang: {front: {x: -0.35, y: 3.75}, back: {x: -0.7, y: 3.6}},
    // Arms can't swing up through a wall in front, so they go up before the runner reaches it.
    reach: {front: {x: 0.1, y: 3.5}, back: {x: -0.4, y: 3.5}, arms: {left: [170, 5], right: [160, 10]}},
    // Standing at a wall the hands rest up on it above the head; they come up on the way there (WALL.raise).
    wall: {arms: {left: [150, 40], right: [138, 58]}},
    rest: [10, 25],
    pull: {front: {x: 0.3, y: 1.1}, back: {x: 0, y: 1.3}},
    step: {front: {x: 1.4, y: 1.5}, back: {x: 0, y: 1.3}, arms: {left: [40, 30], right: [30, 30]}},
    vault: {front: {x: 0.8, y: 1.1}, back: {x: 0.4, y: 1.3}, arms: {left: [45, 20], right: [35, 25]}},
};
// Muscles: every joint follows its pose like a spring, response the rate it closes in at (rad/s), damping 1 just arriving
// and lower swinging past and back like a loose limb; strength softens a muscle towards limp. The spring sets the joint's
// speed rather than pushing with a torque, so it holds a light hand as steadily as a heavy thigh and never runs out of
// strength halfway through a move.
// Stopping against things ahead: the head, torso or pelvis touching something static whose surface faces back against the
// way it's going (more than facing), or the neck pulled further than neck m apart. Pulled, it backs off at backOff m/s.
const JAM = {parts: ['head', 'body', 'belly'], facing: 0.4, neck: 0.08, backOff: 2, headClear: 1.4};
// The runner ducks for something fixed less than duck m over its head at the height it's moving at, looking ahead s of
// travel for a low gap. Once ducked it stands back up only with stand m clear and nothing low for hold ms, so it doesn't
// bob up and down at the mouth of a gap.
const CEILING = {duck: 0.05, stand: 0.4, ahead: 0.35, hold: 300};
// A muscle moves its joint no faster than speed rad/s, so a pose far off is reached in a swing rather than a snap.
const TONE = {leg: {response: 22, damping: 1, speed: 16}, arm: {response: 16, damping: 0.85, speed: 11}, loose: {response: 11, damping: 0.55, speed: 10},
    head: {response: 14, damping: 0.9, speed: 8}, firm: {response: 30, damping: 1, speed: 20}, swing: {response: 26, damping: 0.9, speed: 22}};
const MUSCLE = {maxStep: 0.6, margin: 0.02, jump: 0.6, relaxed: 0.35};
// Joints from the torso outwards, each moving its part against the one it hangs from.
const TREE = [['belly', 'body'], ['head', 'body'],
    ['thigh_left', 'belly'], ['leg_left', 'thigh_left'], ['feet_left', 'leg_left'], ['thigh_right', 'belly'], ['leg_right', 'thigh_right'], ['feet_right', 'leg_right'],
    ['shoulder_left', 'body'], ['arm_left', 'shoulder_left'], ['hand_left', 'arm_left'], ['shoulder_right', 'body'], ['arm_right', 'shoulder_right'], ['hand_right', 'arm_right']];
const FLEX = {ankle: 0.6, ankleMargin: 0.08, hipBack: -40};
// Two-bone IK eases its reach in from soft of the full length: near full stretch the middle joint would swing a long
// way for a small move of the end.
const IK = {soft: 0.95};
// Motor strength drops from full at start to min at end metres of joint stretch.
const COMPLY = {start: 0.12, end: 0.35, min: 0.2};
// The pelvis is a small light circle between the torso and both hip motors. With its own inertia the solver can't
// hold it still and the whole body rocks; while running it turns as if it were part of the torso.
const PELVIS_INERTIA = 20;
// Air poses blend: rising into falling across apex m/s of vertical speed, arms up for a wall over reach ms, into and out
// of the flip's tuck over a tuck share of it.
const BLEND = {apex: 8, reach: 80, tuck: 0.12};
// While running, joints hold several times the force and torque, and a hit has to change a part's speed by impactSpeed
// (m/s) to hurt or bleed: brushing walls and landing jumps shouldn't cost blood.
const TOUGH = {force: 2.5, torque: 1500, impactSpeed: 12};
const CONTROLS = {mirror: 0.3};
// A hit that changes the body's speed by more than impact m/s beyond what gravity and the runner did knocks it down limp,
// and so does landing faster than fall m/s.
// It lies at least minTime ms, and gets up once it has lain still on the ground for settle ms.
const RAGDOLL = {impact: 12, fall: 21, minTime: 700};
// A blow to a limb (its speed changed by more than threshold m/s beyond what the runner did, groundThreshold for a
// standing leg) knocks it loose, fully at full: its muscles give way and take hold again over recover ms. A blow to the
// whole body below a knock-down makes it stagger, its balance loosened the same way.
const REACT = {threshold: 6, groundThreshold: 10, full: 16, give: 0.85, recover: 450, stagger: 3, balance: 0.75};
const LIMB_PARTS = {arm_left: ['shoulder_left', 'arm_left', 'hand_left'], arm_right: ['shoulder_right', 'arm_right', 'hand_right'],
    leg_left: ['thigh_left', 'leg_left', 'feet_left'], leg_right: ['thigh_right', 'leg_right', 'feet_right'], head: ['head']};
// Down and still tumbling faster than speed m/s, the runner braces weakly: arms reaching out the way it's flying and down
// (share of their length), knees drawn up a little, chin tucked. Slowed down, it goes limp.
const BRACE = {speed: 4, strength: 0.25, down: 0.6, reach: {left: 0.9, right: 0.75}, hip: 30, knee: -40, head: 0.3};
// Getting up: over time ms the torso rises from where it lies to standing and turns upright, the muscles coming back
// from strength to full.
const RISE = {time: 800, settle: 350, speed: 1.5, spin: 1.5, ground: 6, gain: 8, accel: 60, turn: 1.4, strength: 0.4};
// A leg that lost its foot walks on the stump, the shin's end held clear m off the ground: it limps, slower and jumping
// lower. The stump only stands a short share of the stride (stump, of the usual) while the good leg stands longer
// (good); the body sinks dip (share of the leg's reach) and pitches forward lurch rad while the stump carries it.
const CRIPPLE = {speed: 5, clear: 0.15, stump: 0.55, good: 1.25, maxDuty: 0.8, dip: 0.09, lurch: 0.14, jump: 0.8, thigh: 25, thighStrength: 0.3};
// With one leg left the runner hops.
const HOP = {speed: 6, period: 380, up: 4.5, ignore: 120, accel: 12, jump: 0.75};
// Holding grab the runner reaches with both hands for the nearest thing within reach m of its shoulders (something fixed
// only from shoulder height up, so it doesn't grab the floor; something loose further down, up to stoop m more, it
// crouches for, though never what it stands on), the wrists going hand m off its surface, and takes hold once a hand is
// within touch m of it (felt along feel rays round the hand). Something loose it carries in front, running and jumping with it. Something fixed in place, a
// bar or a rope tied to something, it holds on to standing and hangs from off the ground: steering pumps the swing at
// pump m/s² while it swings that way (or nearly stands still, within pumpSlack m/s) and is less than maxSwing degrees
// out, the legs swinging with it (fully at legSpeed m/s), and jumping lets go kicked up/forward m/s, with regrab ms
// before it reaches again.
const GRAB = {reach: 3, stoop: 2.5, touch: 0.6, feel: 12, hand: 0.5, pump: 14, pumpSlack: 1, maxSwing: 70, up: 9, forward: 5, regrab: 350, legSpeed: 8,
    carry: {left: [55, 70], right: [45, 80]}, swing: {front: {x: 1.5, y: 2.9}, back: {x: -1.3, y: 3.2}},
    // Holding up raises what's carried towards overhead, holding down lowers it towards low in front, aim times the way from
    // carrying to either a second; let go, it stays where it is. From up high down to low it's a swing that hits.
    raise: {left: [165, 25], right: [155, 35]}, lower: {left: [20, 10], right: [10, 15]}, aim: 4,
    // Something carried keeps up to carryWeight x the runner's weight, rather than the little Character.grab leaves it, so
    // a sword still hits like one.
    carryWeight: 0.25, inset: 0.15};
// Hanging from the rope hat the runner swings loose, hands up at the rope; jumping lets go.
const ROPE = {hands: {left: 1.6, right: 0.9}, gap: 0.3, neck: 400};
// Footsteps from walk to run speed; landings and rolls thud louder from faster falls.
const SOUND = {step: ['footstep-wood-left', 'footstep-wood-right'], stepVolume: [0.08, 0.3], land: ['thud3', 'thud4'], jump: 'swish3', flip: 'whoosh1',
    kick: 'thud1', grab: 'impact-small2', roll: 'swish2'};
const SIDES = ['left', 'right'];
const LIMBS = ['thigh_left', 'leg_left', 'feet_left', 'thigh_right', 'leg_right', 'feet_right', 'shoulder_left', 'arm_left', 'hand_left',
    'shoulder_right', 'arm_right', 'hand_right', 'head'];

const DEG = Math.PI / 180;
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const mix = (a, b, t) => a + (b - a) * t;
const smooth = t => t * t * (3 - 2 * t);
const mixPoint = (a, b, t) => ({x: mix(a.x, b.x, t), y: mix(a.y, b.y, t)});
const mixPose = (a, b, t) => ({front: mixPoint(a.front, b.front, t), back: mixPoint(a.back, b.back, t),
    arms: {left: [mix(a.arms.left[0], b.arms.left[0], t), mix(a.arms.left[1], b.arms.left[1], t)], right: [mix(a.arms.right[0], b.arms.right[0], t), mix(a.arms.right[1], b.arms.right[1], t)]}});
const softReach = (distance, length) => {
    const soft = length * IK.soft;
    return distance <= soft ? distance : soft + (length - soft) * (1 - Math.exp(-(distance - soft) / (length - soft)));
};
const wrapAngle = angle => {
    angle %= Math.PI * 2;
    if (angle > Math.PI) angle -= Math.PI * 2;
    else if (angle < -Math.PI) angle += Math.PI * 2;
    return angle;
};

export class Freerunner extends BaseVehicle {
    constructor(target) {
        super(target);
        this.vehicleName = 'Freerunner';
        this.canJump = true;
        // Flip jumps instead: the runner turns by steering.
        this.flipJumps = true;
        // Up and down mean up and down whichever way the runner faces.
        this.verticalControls = true;
    }
    init() {
        super.init();
        this.character.attachedToVehicle = true;
        MobileController.showRunnerControls();
        this.initRig();
        Object.values(SOUND).flat().forEach(sound => typeof sound === 'string' && AudioManager.preloadSFX(sound));
    }
    initRig() {
        // The character is initialised after us and still holds the editor's lookup.
        const rider = this.character.lookupObject = game.editor.lookupGroups[this.lookupObject.character.body.mySprite.data.subPrefabInstanceName];
        const s = this.character.flipped ? -1 : 1;
        const copy = vector => ({x: vector.x * s, y: vector.y});

        this.legs = {};
        SIDES.forEach(side => {
            const hip = rider[`thigh_${side}_joint`];
            const knee = rider[`leg_${side}_joint`];
            const ankle = rider[`feet_${side}_joint`];
            const thigh = {x: s * (knee.GetLocalAnchorB().x - hip.GetLocalAnchorA().x), y: knee.GetLocalAnchorB().y - hip.GetLocalAnchorA().y};
            const shin = {x: s * (ankle.GetLocalAnchorB().x - knee.GetLocalAnchorA().x), y: ankle.GetLocalAnchorB().y - knee.GetLocalAnchorA().y};
            this.legs[side] = {side, thigh, shin, hipOnBelly: copy(hip.GetLocalAnchorB()), ankleOnFoot: copy(ankle.GetLocalAnchorA()),
                thighLength: Math.hypot(thigh.x, thigh.y), shinLength: Math.hypot(shin.x, shin.y)};
        });
        this.armBones = {};
        SIDES.forEach(side => {
            const shoulder = rider[`shoulder_${side}_joint`];
            const elbow = rider[`arm_${side}_joint`];
            const wrist = rider[`hand_${side}_joint`];
            const upper = {x: s * (elbow.GetLocalAnchorB().x - shoulder.GetLocalAnchorA().x), y: elbow.GetLocalAnchorB().y - shoulder.GetLocalAnchorA().y};
            const fore = {x: s * (wrist.GetLocalAnchorB().x - elbow.GetLocalAnchorA().x), y: wrist.GetLocalAnchorB().y - elbow.GetLocalAnchorA().y};
            this.armBones[side] = {upper, fore, upperLength: Math.hypot(upper.x, upper.y), foreLength: Math.hypot(fore.x, fore.y)};
        });
        const leg = this.legs.left;
        const foot = Box2D.castObject(rider[Humanoid.BODY_PARTS.FEET_LEFT].GetFixtureList().GetShape(), Box2D.b2PolygonShape);
        let sole = -Infinity;
        let toe = -Infinity;
        for (let i = 0; i < foot.get_m_count(); i++) {
            sole = Math.max(sole, foot.get_m_vertices(i).y);
            toe = Math.max(toe, s * foot.get_m_vertices(i).x);
        }
        // The ball of the foot, at the front of the sole, as while facing right.
        this.toe = {x: toe, y: sole};
        // Foot centre above the sole, and the ankle above the sole, while the foot is flat.
        this.footSole = sole;
        this.reach = leg.thighLength + leg.shinLength;
        this.ankleHeight = sole - leg.ankleOnFoot.y;
        // Where the head sits on the torso, and its size, for keeping it off ledges.
        const torso = rider[Humanoid.BODY_PARTS.BODY];
        const head = rider[Humanoid.BODY_PARTS.HEAD];
        const headPoint = torso.GetLocalPoint(head.GetPosition());
        this.head = {forward: s * headPoint.x, up: -headPoint.y, radius: head.GetFixtureList().GetShape().get_m_radius()};

        this.limits = {};
        [...LIMBS, Humanoid.BODY_PARTS.BELLY].forEach(part => {
            const joint = rider[`${part}_joint`];
            if (joint) this.limits[part] = s > 0 ? [joint.GetLowerLimit(), joint.GetUpperLimit()] : [-joint.GetUpperLimit(), -joint.GetLowerLimit()];
        });
        // The waist rests against its limit. Ropes keep the knees within a thigh's length of where the hips sit on the torso,
        // so the waist is locked there: the hips can't shift and stretch them.
        const waist = rider[`${Humanoid.BODY_PARTS.BELLY}_joint`];
        this.waistRest = clamp(s * waist.GetJointAngle(), ...this.limits[Humanoid.BODY_PARTS.BELLY]);
        this.flipped = this.character.flipped;
        this.setControlled(true);
        this.setTough(true);
        // How far the torso sits above the hips standing, for getting back up.
        const hipMiddle = SIDES.map(side => this.hipPoint(this.legs[side])).reduce((sum, point) => ({x: sum.x + point.x / 2, y: sum.y + point.y / 2}), {x: 0, y: 0});
        const torsoAngle = torso.GetAngle();
        this.hipToTorso = (torso.GetPosition().x - hipMiddle.x) * Math.sin(torsoAngle) - (torso.GetPosition().y - hipMiddle.y) * Math.cos(torsoAngle);

        this.moveDir = 0;
        this.moveFrame = -1;
        this.vertical = 0;
        this.verticalInput = 0;
        this.lastVertical = 0;
        this.jumpHeld = false;
        this.jumpBuffer = 0;
        this.jumpIgnore = 0;
        this.jumpsUsed = 0;
        this.jumpRise = null;
        this.ledge = null;
        this.grabCooldown = 0;
        this.limpArms = null;
        this.mirroredControls = false;
        this.wedged = 0;
        this.down = null;
        this.rise = null;
        this.flopHeld = false;
        this.strength = 1;
        this.armTouching = {};
        this.hopTime = 0;
        this.track = null;
        this.wall = null;
        this.lastWall = null;
        this.kicked = null;
        this.ducked = 0;
        this.kickLock = 0;
        this.roll = null;
        this.rollBuffer = 0;
        this.apex = null;
        this.launched = false;
        this.sounds = 0;
        this.airTime = 0;
        this.holdTime = 0;
        this.phase = 0;
        this.bob = 0;
        this.bodyLean = 0;
        this.wallApproach = 0;
        this.braking = false;
        this.skid = 0;
        this.skidLead = null;
        this.accel = 0;
        this.breath = 0;
        this.gaitSpeed = 0;
        this.moving = 0;
        this.flipTime = -1;
        this.flipAngle = 0;
        this.landTime = -1;
        this.landDepth = 0;
        this.fallSpeed = 0;
        this.rigFrame = -1;
        this.pose = {};
        this.trackState = {};
        this.give = {};
        this.stagger = 0;
        this.lastVelocity = null;
        this.stepTime = Settings.physicsTimeStep;
        this.released = false;
        this.grounded = false;
    }
    joint(name) {
        const joint = this.character.lookupObject[name];
        return joint && !joint.destroyed ? joint : null;
    }
    // Limits are kept as while facing right; mirrored joints take them negated and swapped.
    setLimit(part, lower, upper) {
        const joint = this.joint(`${part}_joint`);
        if (!joint) return;
        if (this.flipped) joint.SetLimits(-upper, -lower);
        else joint.SetLimits(lower, upper);
    }
    setFlex(flex) {
        const waist = this.limits[Humanoid.BODY_PARTS.BELLY];
        if (waist) this.setLimit(Humanoid.BODY_PARTS.BELLY, flex ? this.waistRest : waist[0], flex ? this.waistRest : waist[1]);
        ['left', 'right'].forEach(side => {
            const hip = this.limits[`thigh_${side}`];
            const ankle = this.limits[`feet_${side}`];
            if (hip) this.setLimit(`thigh_${side}`, flex ? Math.min(hip[0], FLEX.hipBack * DEG) : hip[0], hip[1]);
            if (ankle) this.setLimit(`feet_${side}`, flex ? -FLEX.ankle : ankle[0], flex ? FLEX.ankle : ankle[1]);
        });
    }
    // Poses a joint for this frame; followPose() moves the body into the pose once everything is posed.
    drive(name, target, strength = 1, tone = null) {
        const limb = Object.keys(LIMB_PARTS).find(key => LIMB_PARTS[key].includes(name.slice(0, -6)));
        strength *= 1 - REACT.give * (this.give[limb] ?? 0);
        this.pose[name] = {target, strength: strength * this.strength, tone: tone ?? (name.startsWith('head') ? TONE.head : name.startsWith('thigh') || name.startsWith('leg') || name.startsWith('feet') ? TONE.leg : TONE.arm)};
    }
    // From the torso outwards, each posed joint's part turns towards its pose at the speed its muscle's spring gives,
    // relative to the part it hangs from, and moves with that part at the joint. Muscles pull between parts of the body,
    // so the body as a whole keeps its momentum and spin: a limb swung forward pushes the rest of it back.
    followPose() {
        const rider = this.character.lookupObject;
        const dt = this.stepTime;
        const frame = simulationFrame();
        // What's carried moves with the hand holding it, as part of the arm: left to the weld alone, its weight would drag
        // the hand about and the arm could never hold it still or swing it.
        const carried = this.anchored || this.carryTouch?.touching ? [] : SIDES.map(side => {
            const hand = rider[`hand_${side}`];
            const body = this.heldBy(side)?.GetBodyB();
            return hand && body && !hand.snapped && !hand.destroyed ? {hand, body} : null;
        }).filter(Boolean).filter((hold, i, all) => all.findIndex(other => other.body === hold.body) === i);
        const parts = [...this.parts(), ...carried.map(hold => hold.body)];
        const momentum = () => {
            let mass = 0;
            let x = 0;
            let y = 0;
            let vx = 0;
            let vy = 0;
            parts.forEach(body => {
                const m = body.GetMass();
                const center = body.GetWorldCenter();
                const velocity = body.GetLinearVelocity();
                mass += m;
                x += m * center.x;
                y += m * center.y;
                vx += m * velocity.x;
                vy += m * velocity.y;
            });
            x /= mass;
            y /= mass;
            let spin = 0;
            let inertia = 0;
            parts.forEach(body => {
                const m = body.GetMass();
                const center = body.GetWorldCenter();
                const velocity = body.GetLinearVelocity();
                const rx = center.x - x;
                const ry = center.y - y;
                const own = body.GetInertia() - m * body.GetLocalCenter().LengthSquared();
                spin += own * body.GetAngularVelocity() + m * (rx * velocity.y - ry * velocity.x);
                inertia += own + m * (rx * rx + ry * ry);
            });
            return {mass, x, y, vx, vy, spin, inertia};
        };
        const before = momentum();
        const vector = (body, point) => {
            const center = body.GetWorldCenter();
            return {x: point.x - center.x, y: point.y - center.y};
        };
        TREE.forEach(([part, parentName]) => {
            const name = `${part}_joint`;
            const command = this.pose[name];
            const joint = this.joint(name);
            const body = rider[part];
            const parent = rider[parentName];
            if (!command || !joint || !body || !parent || body.destroyed || parent.destroyed || body.snapped || parent.snapped) return;
            joint.EnableMotor(false);
            let target = wrapAngle(command.target);
            if (joint.IsLimitEnabled()) {
                const lower = joint.GetLowerLimit();
                const upper = joint.GetUpperLimit();
                const margin = Math.min(MUSCLE.margin, (upper - lower) / 2);
                target = clamp(target, lower + margin, upper - margin);
            }
            // The pose's own movement carries on; a pose that jumps is only closed in on.
            const last = this.trackState[name];
            const moved = last && last.frame === frame - 1 ? target - last.target : 0;
            const follow = Math.abs(moved) < MUSCLE.jump ? moved / dt : 0;
            this.trackState[name] = {target, frame};
            const {response, damping, speed: maxSpeed} = command.tone;
            const blend = Math.min(1, 2 * damping * response * dt);
            // A long step (fast game speed) would close more than the whole gap in one go and overshoot.
            const rate = Math.min(response * response * dt / blend, MUSCLE.maxStep / dt);
            const angle = joint.GetJointAngle();
            const speed = joint.GetJointSpeed();
            const next = speed + blend * command.strength * (clamp(follow + rate * (target - angle), -maxSpeed, maxSpeed) - speed);
            // Joint angle is parent minus child when the child is body A.
            const sign = Box2D.getPointer(joint.GetBodyA()) === Box2D.getPointer(body) ? -1 : 1;
            const parentSpin = parent.GetAngularVelocity();
            const spin = parentSpin + sign * next;
            const parentVelocity = parent.GetLinearVelocity();
            const parentAnchor = vector(parent, sign < 0 ? joint.GetAnchorB() : joint.GetAnchorA());
            const anchor = vector(body, sign < 0 ? joint.GetAnchorA() : joint.GetAnchorB());
            vec1.Set(parentVelocity.x - parentSpin * parentAnchor.y + spin * anchor.y, parentVelocity.y + parentSpin * parentAnchor.x - spin * anchor.x);
            body.SetLinearVelocity(vec1);
            body.SetAngularVelocity(spin);
        });
        carried.forEach(({hand, body}) => {
            const spin = hand.GetAngularVelocity();
            const velocity = hand.GetLinearVelocity();
            const handCenter = hand.GetWorldCenter();
            const center = body.GetWorldCenter();
            vec1.Set(velocity.x - spin * (center.y - handCenter.y), velocity.y + spin * (center.x - handCenter.x));
            body.SetLinearVelocity(vec1);
            body.SetAngularVelocity(spin);
        });
        const after = momentum();
        this.lastVelocity = new Map();
        const dvx = (before.vx - after.vx) / after.mass;
        const dvy = (before.vy - after.vy) / after.mass;
        const turn = (before.spin - after.spin) / after.inertia;
        parts.forEach(body => {
            const center = body.GetWorldCenter();
            const velocity = body.GetLinearVelocity();
            vec1.Set(velocity.x + dvx - turn * (center.y - after.y), velocity.y + dvy + turn * (center.x - after.x));
            body.SetLinearVelocity(vec1);
            body.SetAngularVelocity(body.GetAngularVelocity() + turn);
            const set = body.GetLinearVelocity();
            this.lastVelocity.set(body, {x: set.x, y: set.y});
        });
    }
    // How hard each limb was hit since the last frame: the change in its speed that its own posing and gravity don't explain.
    react(gx, gy, dt, ms) {
        const rider = this.character.lookupObject;
        Object.entries(LIMB_PARTS).forEach(([limb, names]) => {
            let blow = 0;
            names.forEach(name => {
                const body = rider[name];
                const last = body && !body.destroyed && this.lastVelocity?.get(body);
                if (!last) return;
                const velocity = body.GetLinearVelocity();
                blow = Math.max(blow, Math.hypot(velocity.x - last.x - gx * dt, velocity.y - last.y - gy * dt));
            });
            const threshold = limb.startsWith('leg') && this.grounded ? REACT.groundThreshold : REACT.threshold;
            this.give[limb] = Math.max(clamp((blow - threshold) / (REACT.full - threshold), 0, 1), (this.give[limb] ?? 0) - ms / REACT.recover);
        });
        this.lastVelocity = null;
    }
    releaseMotors() {
        LIMBS.forEach(part => this.joint(`${part}_joint`)?.EnableMotor(false));
    }
    // The limbs only animate, so they slide freely instead of shoving the body around or dragging it along walls.
    setFriction(controlled) {
        this.character.lookupObject._bodies.forEach(body => {
            if (body.destroyed) return;
            for (let fixture = body.GetFixtureList(); Box2D.getPointer(fixture) !== Box2D.getPointer(Box2D.NULL); fixture = fixture.GetNext()) {
                if (controlled) fixture.originalFriction ??= fixture.GetFriction();
                fixture.SetFriction(controlled ? 0 : fixture.originalFriction ?? fixture.GetFriction());
            }
            for (let edge = body.GetContactList(); Box2D.getPointer(edge) !== Box2D.getPointer(Box2D.NULL); edge = edge.get_next()) edge.contact.ResetFriction();
        });
    }
    setTough(controlled) {
        const character = this.character;
        if (controlled) {
            this.jointLimits = {forces: character.jointMaxForces, torque: character.jointMaxTorque};
            character.jointMaxForces = character.jointMaxForces.map(force => force * TOUGH.force * TOUGH.force);
            character.jointMaxTorque = TOUGH.torque;
        } else if (this.jointLimits) {
            character.jointMaxForces = this.jointLimits.forces;
            character.jointMaxTorque = this.jointLimits.torque;
        }
        character.lookupObject._bodies.forEach(body => {
            if (controlled) body.impactSpeed = TOUGH.impactSpeed;
            else delete body.impactSpeed;
        });
    }
    setPelvisInertia(controlled) {
        const belly = this.character.lookupObject[Humanoid.BODY_PARTS.BELLY];
        if (!belly) return;
        belly.ResetMassData();
        if (!controlled) return;
        const data = new Box2D.b2MassData();
        belly.GetMassData(data);
        const center = data.get_center();
        const offset = data.get_mass() * (center.x * center.x + center.y * center.y);
        data.set_I((data.get_I() - offset) * PELVIS_INERTIA + offset);
        belly.SetMassData(data);
        Box2D.destroy(data);
    }
    // Controlled, the runner's own posing holds the body; otherwise it's a plain ragdoll.
    setControlled(controlled) {
        if (!controlled) this.releaseMotors();
        this.setFlex(controlled);
        this.setFriction(controlled);
        this.setPelvisInertia(controlled);
        this.trackState = {};
    }
    release() {
        if (this.released) return;
        this.letGoHold();
        this.restoreHeldCollision(true);
        this.heldBodies = null;
        this.released = true;
        this.setControlled(false);
        this.setTough(false);
        CAUGHT.forEach(limb => limb.forEach(part => {
            const body = this.character.lookupObject[part];
            if (body?.slipping && !body.destroyed) this.setSlipping(body, false);
        }));
    }
    eject() {
        super.eject();
        this.release();
    }
    // Through a portal or moved by a trigger: whatever the runner held or was turning towards is left behind.
    onCharacterTeleported() {
        if (this.ledge) this.letGo(1, 0);
        this.letGoHold();
        this.wall = null;
        this.lastWall = null;
        this.roll = null;
        this.flipTime = -1;
        this.apex = null;
    }
    // A trigger's impulse moves every part alike: a launch to fly with, not a hit.
    setLaunched() {
        this.launched = true;
    }
    sound(name, volume, position) {
        // A little pitch variation that's the same on every replay.
        this.sounds++;
        AudioManager.playSFX(name, volume, 0.92 + 0.16 * (this.sounds * 0.618 % 1), position);
    }
    // A loose thing too thin along forward to stand on, a rope segment or a pole, or too light to hold the runner:
    // something to grab, not a ledge.
    slender(body, forward) {
        if (body.GetType() !== Box2D.b2_dynamicBody) return false;
        if (body.GetMass() < this.bodyMass * LEDGE.minMass) return true;
        let lower = Infinity;
        let upper = -Infinity;
        const extend = (point, radius = 0) => {
            const along = point.x * forward.x + point.y * forward.y;
            lower = Math.min(lower, along - radius);
            upper = Math.max(upper, along + radius);
        };
        for (let fixture = body.GetFixtureList(); Box2D.getPointer(fixture) !== Box2D.getPointer(Box2D.NULL); fixture = fixture.GetNext()) {
            const shape = fixture.GetShape();
            if (shape.GetType() === Box2D.b2Shape.e_circle) {
                const circle = Box2D.castObject(shape, Box2D.b2CircleShape);
                extend(body.GetWorldPoint(circle.get_m_p()), circle.get_m_radius());
            } else if (shape.GetType() === Box2D.b2Shape.e_polygon) {
                const polygon = Box2D.castObject(shape, Box2D.b2PolygonShape);
                for (let i = 0; i < polygon.get_m_count(); i++) extend(body.GetWorldPoint(polygon.get_m_vertices(i)));
            }
        }
        return upper - lower < LEDGE.minWidth;
    }
    // Holding something fixed, with the grip behind and the shoulders already an arm's reach from it.
    atArmsLength(arms, forward) {
        if (!this.holding() || !this.anchored || arms.length === 0) return false;
        const grip = this.gripPoint();
        const shoulder = this.shoulderPoint(arms);
        return (grip.x - shoulder.x) * forward.x + (grip.y - shoulder.y) * forward.y < 0 && Math.hypot(grip.x - shoulder.x, grip.y - shoulder.y) > GRAB.reach;
    }
    holding() {
        return SIDES.some(side => this.heldBy(side));
    }
    heldBy(side) {
        const joint = this.character[side === 'left' ? 'grabJointLeft' : 'grabJointRight'];
        return joint && !joint.destroyed ? joint : null;
    }
    letGoHold() {
        if (this.holding()) this.character.release();
    }
    // Where the hands hold on: the middle of their grips.
    gripPoint() {
        const grips = SIDES.map(side => this.heldBy(side)).filter(Boolean).map(joint => {
            const anchor = joint.GetAnchorB();
            return {x: anchor.x, y: anchor.y};
        });
        return {x: grips.reduce((sum, grip) => sum + grip.x, 0) / grips.length, y: grips.reduce((sum, grip) => sum + grip.y, 0) / grips.length};
    }
    feetDown(legs, up) {
        if (legs.length === 0) return false;
        const hips = legs.map(side => this.hipPoint(this.legs[side]));
        const hip = {x: hips.reduce((sum, point) => sum + point.x, 0) / hips.length, y: hips.reduce((sum, point) => sum + point.y, 0) / hips.length};
        return Boolean(this.probe(hip.x, hip.y, up, this.reach * POSE.stand + this.ankleHeight + HOVER.snap));
    }
    // The nearest thing to take hold of within reach of the shoulders: each fixture around them is aimed at, at the
    // point of its bounds nearest the shoulders, so even a thin rope is found.
    findHold(arms, up, forward) {
        const shoulder = this.shoulderPoint(arms);
        const world = this.character.lookupObject[Humanoid.BODY_PARTS.BODY].GetWorld();
        const aabb = new Box2D.b2AABB();
        const range = GRAB.reach + GRAB.stoop;
        aabb.get_lowerBound().Set(shoulder.x - range, shoulder.y - range);
        aabb.get_upperBound().Set(shoulder.x + range, shoulder.y + range);
        const aims = [];
        const query = Object.assign(new Box2D.JSQueryCallback(), {
            ReportFixture(fixturePointer) {
                const fixture = Box2D.wrapPointer(fixturePointer, Box2D.b2Fixture);
                const body = fixture.GetBody();
                if (fixture.IsSensor() || body.mainCharacter || body.isPhysicsCamera || body.isPortalGhost || body.isPortalShadow) return true;
                const box = fixture.GetAABB(0);
                const lower = box.get_lowerBound();
                const upper = box.get_upperBound();
                // A little inside the bounds, so the ray doesn't graze past a corner.
                const inset = {x: (upper.x - lower.x) * GRAB.inset, y: (upper.y - lower.y) * GRAB.inset};
                aims.push({x: clamp(shoulder.x, lower.x + inset.x, upper.x - inset.x), y: clamp(shoulder.y, lower.y + inset.y, upper.y - inset.y)});
                return true;
            },
        });
        world.QueryAABB(query, aabb);
        Box2D.destroy(query);
        Box2D.destroy(aabb);
        let best = null;
        aims.forEach(aim => {
            const length = Math.hypot(aim.x - shoulder.x, aim.y - shoulder.y);
            if (length > range || length < 0.01) return;
            const hit = this.cast(shoulder.x, shoulder.y, (aim.x - shoulder.x) / length, (aim.y - shoulder.y) / length, range);
            if (!hit || hit.body === this.groundBody || this.supportBodies?.has(hit.body)) return;
            const height = (hit.x - shoulder.x) * up.x + (hit.y - shoulder.y) * up.y;
            const fixed = hit.body.GetType() !== Box2D.b2_dynamicBody;
            // Loose things are picked up in front, not behind.
            const ahead = (hit.x - shoulder.x) * forward.x + (hit.y - shoulder.y) * forward.y;
            if (fixed ? height < GRAB.hand || hit.distance > GRAB.reach : ahead < 0 || hit.distance > GRAB.reach && height > 0) return;
            if (!best || hit.distance < best.distance) best = hit;
        });
        // Out of reach standing, down low: the runner crouches for it.
        this.stoop = Boolean(best && best.distance > GRAB.reach);
        return best;
    }
    // Reaching for something to hold while grab is held, and holding it once got: carried in front, or followed.
    updateHands(c) {
        const {up, right, arms} = c;
        this.stoop = false;
        if (this.holding()) {
            const grip = this.gripPoint();
            arms.forEach(side => {
                const joint = this.heldBy(side);
                if (joint && joint.GetBodyB().GetType() === Box2D.b2_dynamicBody) {
                    const toward = (this.aim > 0 ? GRAB.raise : GRAB.lower)[side];
                    // Against the world it gives, like a hand touching it.
                    const strength = this.carryTouch?.touching ? MUSCLE.relaxed : 1;
                    this.driveArm(side, GRAB.carry[side].map((angle, i) => mix(angle, toward[i], Math.abs(this.aim))), strength, TONE.swing);
                    // A firm wrist: what's held turns with the forearm.
                    this.drive(`hand_${side}_joint`, 0, strength, TONE.firm);
                }
                else this.reachArm(side, grip, 0, 180, MUSCLE.relaxed);
            });
            return;
        }
        if (!this.grabHeld || this.regrab > 0 || arms.length === 0 || this.ledge || this.wall || this.roll) return;
        const s = this.flipped ? -1 : 1;
        const hold = this.findHold(arms, up, {x: right.x * s, y: right.y * s});
        if (!hold) return;
        // The wrist goes a hand's length off the surface, so the hand lands on it rather than pushing into it.
        const normal = Math.hypot(hold.nx, hold.ny) || 1;
        const wrist = {x: hold.x + hold.nx / normal * GRAB.hand, y: hold.y + hold.ny / normal * GRAB.hand};
        arms.forEach(side => this.reachArm(side, wrist));
        const rider = this.character.lookupObject;
        // A hand that has got to the thing, wherever on it, takes hold: something of it within touch m all round the hand.
        if (arms.some(side => {
            const position = rider[`hand_${side}`].GetPosition();
            const hand = {x: position.x, y: position.y};
            for (let i = 0; i < GRAB.feel; i++) {
                const angle = i / GRAB.feel * Math.PI * 2;
                if (this.cast(hand.x, hand.y, Math.cos(angle), Math.sin(angle), GRAB.touch)?.body === hold.body) return true;
            }
            return false;
        })) {
            this.character.grab();
            this.keepAnchoredWeight();
        }
    }
    // Grabbing makes what's held light to carry. Something fixed in place through its joints, a rope or a chain, keeps
    // its weight: made light, it would stretch like rubber under the runner.
    keepAnchoredWeight() {
        this.anchored = false;
        SIDES.forEach(side => {
            const joint = this.heldBy(side);
            if (!joint) return;
            const bodies = this.heldGroup([joint.GetBodyB()]);
            if (bodies.every(body => body.GetType() === Box2D.b2_dynamicBody)) {
                this.carryWeight(bodies);
                return;
            }
            this.anchored = true;
            bodies.forEach(body => {
                if (body.oldDensities === undefined) return;
                let i = 0;
                for (let fixture = body.GetFixtureList(); Box2D.getPointer(fixture) !== Box2D.getPointer(Box2D.NULL); fixture = fixture.GetNext()) fixture.SetDensity(body.oldDensities[i++]);
                body.ResetMassData();
                delete body.oldDensities;
            });
        });
    }
    // Something carried keeps its own weight up to a share of the runner's: a sword hits like a sword, a crate doesn't pull
    // the arms down.
    carryWeight(bodies) {
        const lightened = body => body.oldDensities?.[0] > 0 ? body.GetFixtureList().GetDensity() / body.oldDensities[0] : 1;
        const weight = bodies.reduce((sum, body) => sum + body.GetMass() / lightened(body), 0);
        const share = Math.min(1, GRAB.carryWeight * this.bodyMass / (weight || 1));
        bodies.forEach(body => {
            if (body.oldDensities === undefined) return;
            let i = 0;
            for (let fixture = body.GetFixtureList(); Box2D.getPointer(fixture) !== Box2D.getPointer(Box2D.NULL); fixture = fixture.GetNext()) fixture.SetDensity(body.oldDensities[i++] * share);
            body.ResetMassData();
        });
    }
    // A grip broken without letting go, gone with what was held, is let go properly: left behind, the character would
    // think it still holds on and never take hold of anything again.
    clearBrokenGrips() {
        const character = this.character;
        [['left', 'Left'], ['right', 'Right']].forEach(([side, key]) => {
            if (!character[`grabJoint${key}`]?.destroyed) return;
            if (character[`grabBody${key}`]?.destroyed) character[`grabBody${key}`] = null;
            character.release(side === 'left', side === 'right');
        });
    }
    // What's held doesn't collide with the runner holding it. Let go, it does again once clear of the body, rather than
    // jolting apart from inside it.
    updateHeldCollision() {
        const held = this.holding() ? this.heldGroup(SIDES.map(side => this.heldBy(side)?.GetBodyB()).filter(Boolean))
            .filter(body => body.GetType() === Box2D.b2_dynamicBody) : [];
        // Nor is it stood on, walked into or reached for: standing on what it holds, the runner would lift itself.
        this.heldBodies = new Set(held);
        const group = this.character.lookupObject[Humanoid.BODY_PARTS.BODY]?.GetFixtureList().GetFilterData().get_groupIndex();
        if (!(group < 0)) return;
        this.heldGroups ??= new Map();
        held.forEach(body => {
            if (this.heldGroups.has(body)) return;
            const groups = [];
            for (let fixture = body.GetFixtureList(); Box2D.getPointer(fixture) !== Box2D.getPointer(Box2D.NULL); fixture = fixture.GetNext()) {
                const filter = fixture.GetFilterData();
                groups.push(filter.get_groupIndex());
                filter.set_groupIndex(group);
                fixture.SetFilterData(filter);
            }
            this.heldGroups.set(body, groups);
        });
        this.restoreHeldCollision(false, held);
    }
    restoreHeldCollision(all, held = []) {
        this.heldGroups?.forEach((groups, body) => {
            if (held.includes(body)) return;
            if (!body.destroyed && !all && this.overlapsRunner(body)) return;
            let i = 0;
            if (!body.destroyed) for (let fixture = body.GetFixtureList(); Box2D.getPointer(fixture) !== Box2D.getPointer(Box2D.NULL) && i < groups.length; fixture = fixture.GetNext()) {
                const filter = fixture.GetFilterData();
                filter.set_groupIndex(groups[i++]);
                fixture.SetFilterData(filter);
            }
            this.heldGroups.delete(body);
        });
    }
    overlapsRunner(body) {
        const boxes = [];
        this.parts().forEach(part => {
            for (let fixture = part.GetFixtureList(); Box2D.getPointer(fixture) !== Box2D.getPointer(Box2D.NULL); fixture = fixture.GetNext()) {
                const box = fixture.GetAABB(0);
                boxes.push([box.get_lowerBound().x, box.get_lowerBound().y, box.get_upperBound().x, box.get_upperBound().y]);
            }
        });
        for (let fixture = body.GetFixtureList(); Box2D.getPointer(fixture) !== Box2D.getPointer(Box2D.NULL); fixture = fixture.GetNext()) {
            const box = fixture.GetAABB(0);
            const [x0, y0, x1, y1] = [box.get_lowerBound().x, box.get_lowerBound().y, box.get_upperBound().x, box.get_upperBound().y];
            if (boxes.some(([a0, b0, a1, b1]) => x0 < a1 && a0 < x1 && y0 < b1 && b0 < y1)) return true;
        }
        return false;
    }
    // Whether what's carried touches the world, and whether it rests on something below it or is pressed from above.
    heldTouch() {
        const up = {x: Math.sin(this.upAngle ?? 0), y: -Math.cos(this.upAngle ?? 0)};
        const bodies = this.heldGroup(SIDES.map(side => this.heldBy(side)?.GetBodyB()).filter(Boolean));
        const touch = {touching: false, below: false, above: false};
        bodies.forEach(body => {
            for (let edge = body.GetContactList(); Box2D.getPointer(edge) !== Box2D.getPointer(Box2D.NULL); edge = edge.get_next()) {
                const contact = edge.contact;
                if (!contact.IsTouching() || !contact.IsEnabled() || contact.GetFixtureA().IsSensor() || contact.GetFixtureB().IsSensor()) continue;
                if (edge.other.mainCharacter || bodies.includes(edge.other)) continue;
                touch.touching = true;
                contact.GetWorldManifold(heldManifold);
                const normal = heldManifold.get_normal();
                const into = Box2D.getPointer(contact.GetFixtureB().GetBody()) === Box2D.getPointer(body) ? 1 : -1;
                const lift = into * (normal.x * up.x + normal.y * up.y);
                if (lift > 0.5) touch.below = true;
                if (lift < -0.5) touch.above = true;
            }
        });
        return touch;
    }
    // What's held and everything jointed to it, the runner aside.
    heldGroup(held) {
        const seen = new Set(held.map(body => Box2D.getPointer(body)));
        const bodies = [...held];
        for (let i = 0; i < bodies.length; i++) {
            for (let edge = bodies[i].GetJointList(); Box2D.getPointer(edge) !== Box2D.getPointer(Box2D.NULL); edge = edge.get_next()) {
                const other = edge.other;
                if (other.mainCharacter || seen.has(Box2D.getPointer(other))) continue;
                seen.add(Box2D.getPointer(other));
                bodies.push(other);
            }
        }
        return bodies;
    }
    // Turning mirrors the rig, which tears the grip off. What's carried turns round with the runner: mirrored to its other
    // side and to the mirrored spot in front, it's taken hold of again where the hands were. With something fixed in the way
    // there it's dropped.
    turnCarrying() {
        const grips = SIDES.map(side => {
            const joint = this.heldBy(side);
            if (!joint) return null;
            const anchor = joint.GetAnchorB();
            return {hand: this.character.lookupObject[`hand_${side}`], body: joint.GetBodyB(), x: anchor.x, y: anchor.y};
        }).filter(Boolean);
        const torso = this.character.lookupObject[Humanoid.BODY_PARTS.BODY];
        const center = {x: torso.GetPosition().x, y: torso.GetPosition().y};
        const torsoAngle = torso.GetAngle();
        const axis = {x: -Math.sin(torsoAngle), y: Math.cos(torsoAngle)};
        const mirror = point => {
            const dx = point.x - center.x;
            const dy = point.y - center.y;
            const along = 2 * (dx * axis.x + dy * axis.y);
            return {x: center.x + along * axis.x - dx, y: center.y + along * axis.y - dy};
        };
        const bodies = this.heldGroup([...new Set(grips.map(grip => grip.body))]);
        const clear = bodies.every(body => {
            for (let fixture = body.GetFixtureList(); Box2D.getPointer(fixture) !== Box2D.getPointer(Box2D.NULL); fixture = fixture.GetNext()) {
                const shape = fixture.GetShape();
                const points = [];
                if (shape.GetType() === Box2D.b2Shape.e_polygon) {
                    const polygon = Box2D.castObject(shape, Box2D.b2PolygonShape);
                    for (let i = 0; i < polygon.get_m_count(); i++) {
                        const point = body.GetWorldPoint(polygon.get_m_vertices(i));
                        points.push({x: point.x, y: point.y});
                    }
                } else {
                    const point = body.GetWorldPoint(Box2D.castObject(shape, Box2D.b2CircleShape).get_m_p());
                    points.push({x: point.x, y: point.y});
                }
                if (points.map(mirror).some(point => {
                    const length = Math.hypot(point.x - center.x, point.y - center.y);
                    return length > 0.01 && this.cast(center.x, center.y, (point.x - center.x) / length, (point.y - center.y) / length, length, true);
                })) return false;
            }
            return true;
        });
        this.letGoHold();
        this.character.flip();
        if (!clear) {
            this.regrab = GRAB.regrab;
            return;
        }
        // Mirrored in place, then moved as a whole so the held body lands where the mirror puts it.
        const held = grips[0].body;
        const before = {x: held.GetPosition().x, y: held.GetPosition().y, angle: held.GetAngle()};
        this.mirrorCarried(bodies);
        const after = {x: held.GetPosition().x, y: held.GetPosition().y};
        const target = mirror(before);
        const turn = 2 * torsoAngle - before.angle - held.GetAngle();
        const cos = Math.cos(turn);
        const sin = Math.sin(turn);
        bodies.forEach(body => {
            const position = body.GetPosition();
            vec1.Set(target.x + cos * (position.x - after.x) - sin * (position.y - after.y), target.y + sin * (position.x - after.x) + cos * (position.y - after.y));
            body.SetTransform(vec1, body.GetAngle() + turn);
        });
        grips.forEach(grip => {
            if (!grip.hand || grip.hand.snapped || grip.hand.destroyed) return;
            const point = mirror(grip);
            vec2.Set(point.x, point.y);
            this.character.attachHand(grip.hand, grip.body, vec2);
        });
        this.keepAnchoredWeight();
    }
    // A character, bare or inside an NPC such as a baby, mirrors as it does at play: an NPC's own flip lays its limbs out
    // as placed in the editor. A weapon flips itself to the other side. Anything else, a vehicle's parts too (a vehicle's
    // flip turns the player's vehicle), has its shapes and art mirrored.
    mirrorCarried(bodies) {
        const flips = new Map();
        const plain = bodies.filter(body => {
            const prefab = game.editor.retrieveClassFromBody(body);
            const character = prefab instanceof Humanoid ? prefab : prefab?.character instanceof Humanoid ? prefab.character : null;
            if (character) {
                flips.set(prefab, () => {
                    // A Character's flip turns the vehicle it rides unless told not to.
                    character.flip(true);
                    if (prefab !== character) prefab.flipped = !prefab.flipped;
                });
                return false;
            }
            if (typeof prefab?.flip !== 'function' || prefab.flip.length !== 1 || typeof prefab.flipped !== 'boolean') return true;
            flips.set(prefab, () => prefab.flip(!prefab.flipped));
            return false;
        });
        flips.forEach(flip => flip());
        if (plain.length > 0) game.editor.mirrorPrefab({lookupObject: {_bodies: plain, _textures: [], _joints: [], center: plain[0]}}, 'center');
    }
    // Hanging from something held: the body swings under the grip, pumped by steering; jumping lets go.
    updateSwing(c) {
        const {parts, torso, up, right, upAngle, legs, arms, move, dt, comX, comY, velocityX, velocityY, spinSpeed} = c;
        const s = this.flipped ? -1 : 1;
        this.grounded = false;
        this.wall = null;
        this.lastWall = null;
        this.flipTime = -1;
        this.jumpsUsed = 0;
        this.jumpRise = null;
        this.airTime = JUMP.coyote + 1;
        this.apex = comX * up.x + comY * up.y;
        const grip = this.gripPoint();
        const length = Math.hypot(grip.x - comX, grip.y - comY) || 1;
        const toGrip = {x: (grip.x - comX) / length, y: (grip.y - comY) / length};
        // Along the swing, the way the runner faces.
        let tangent = {x: -toGrip.y, y: toGrip.x};
        if (tangent.x * right.x * s + tangent.y * right.y * s < 0) tangent = {x: -tangent.x, y: -tangent.y};
        const swing = velocityX * tangent.x + velocityY * tangent.y;
        if (this.jumpBuffer > 0) {
            this.letGoHold();
            const rise = velocityX * up.x + velocityY * up.y;
            const ahead = (move || s) * GRAB.forward;
            this.push(parts, up.x * Math.max(0, GRAB.up - rise) + right.x * ahead, up.y * Math.max(0, GRAB.up - rise) + right.y * ahead);
            this.jumpsUsed = 1;
            this.jumpBuffer = 0;
            this.jumpIgnore = JUMP.groundIgnore;
            this.jumpRise = this.jumpHeld ? 'held' : 'cut';
            this.regrab = GRAB.regrab;
            this.sound(SOUND.jump, 0.15, torso.GetPosition());
            return;
        }
        const out = Math.acos(clamp(-(toGrip.x * up.x + toGrip.y * up.y), -1, 1));
        if (move !== 0 && swing * move * s > -GRAB.pumpSlack && out < GRAB.maxSwing * DEG) this.push(parts, tangent.x * move * s * GRAB.pump * dt, tangent.y * move * s * GRAB.pump * dt);
        const error = wrapAngle(Math.atan2(toGrip.x, -toGrip.y) - torso.GetAngle());
        this.spin(parts, (clamp(error * UPRIGHT.airRate, -UPRIGHT.maxSpin, UPRIGHT.maxSpin) - spinSpeed) * UPRIGHT.response, comX, comY);
        this.pelvisAngle = torso.GetAngle() + error + s * this.waistRest;
        // The legs swing ahead going forwards and trail coming back.
        const k = 0.5 + 0.5 * clamp(swing / GRAB.legSpeed, -1, 1);
        const pose = {front: mixPoint(GRAB.swing.back, GRAB.swing.front, k), back: mixPoint(GRAB.swing.back, GRAB.swing.front, k * 0.7)};
        this.poseLegs(legs, torso, pose);
        arms.forEach(side => this.reachArm(side, grip));
        this.lookAt(HEAD.air);
    }
    // Hanging from the rope hat the rope carries the body: it swings loose, hands up at the rope.
    updateCarried(c) {
        const {torso, up, legs, arms, comX, comY} = c;
        const hat = this.character.hat;
        if (this.ledge) this.letGo(1, 0);
        this.wall = null;
        this.lastWall = null;
        this.roll = null;
        this.flipTime = -1;
        this.grounded = false;
        this.jumpsUsed = 1;
        this.jumpRise = null;
        this.airTime = JUMP.coyote + 1;
        this.apex = comX * up.x + comY * up.y;
        // Jumping lets go, and the air jump carries the swing on.
        if (this.jumpBuffer > 0) {
            hat.detachRope();
            return;
        }
        const s = this.flipped ? -1 : 1;
        this.pelvisAngle = torso.GetAngle() + s * this.waistRest;
        this.poseLegs(legs, torso, AIR.fall);
        const head = this.character.lookupObject[Humanoid.BODY_PARTS.HEAD];
        const end = hat.ropeEnd?.GetPosition();
        if (!head || !end) return;
        const headX = head.GetPosition().x;
        const headY = head.GetPosition().y;
        const length = Math.hypot(end.x - headX, end.y - headY) || 1;
        const along = {x: (end.x - headX) / length, y: (end.y - headY) / length};
        const ahead = {x: -along.y * s, y: along.x * s};
        arms.forEach(side => this.reachArm(side, {
            x: headX + along.x * ROPE.hands[side] + ahead.x * ROPE.gap,
            y: headY + along.y * ROPE.hands[side] + ahead.y * ROPE.gap,
        }));
        // The rope holds the head; the neck holds the torso in line with the rope.
        const neck = this.joint('head_joint');
        if (neck) this.drive('head_joint', neck.GetJointAngle() + wrapAngle(Math.atan2(along.x, -along.y) - torso.GetAngle()), 1, TONE.firm);
    }

    accelerate(dir) {
        this.verticalInput = dir;
    }
    stopAccelerate() {
        this.verticalInput = 0;
    }
    lean(dir) {
        this.moveDir = dir;
        this.moveFrame = simulationFrame();
    }
    jump(held) {
        if (held && !this.jumpHeld) this.jumpBuffer = JUMP.buffer;
        if (!held && this.jumpHeld) this.releaseJump = true;
        this.jumpHeld = held;
    }
    grab(held) {
        this.grabHeld = held;
    }
    // Holding eject goes limp; letting go gets back up.
    flop(held) {
        this.flopHeld = held;
        if (held && !this.down && !this.released) this.knockDown();
    }
    knockDown() {
        this.letGoHold();
        this.down = {time: 0, still: 0};
        this.rise = null;
        this.ledge = null;
        this.wall = null;
        this.roll = null;
        this.flipTime = -1;
        this.jumpBuffer = 0;
        this.jumpRise = null;
        this.strength = 1;
        this.setControlled(false);
    }
    // Lying limp until the body has come to rest on something it can stand up from.
    updateDown(c) {
        const {up, comX, comY, velocityX, velocityY, spinSpeed, ms, legs, arms} = c;
        const s = this.flipped ? -1 : 1;
        const down = this.down;
        down.time += ms;
        const ground = this.probe(comX, comY, up, RISE.ground);
        const carried = ground ? this.carriedVelocity(ground) : {x: 0, y: 0};
        const speed = Math.hypot(velocityX - carried.x, velocityY - carried.y);
        const still = ground && speed < RISE.speed && Math.abs(spinSpeed) < RISE.spin;
        down.still = still ? down.still + ms : 0;
        if (!this.flopHeld && speed > BRACE.speed) {
            // The hands reach out where the body is heading and down, to catch it.
            const length = Math.hypot(velocityX - carried.x, velocityY - carried.y) || 1;
            const reachX = (velocityX - carried.x) / length - up.x * BRACE.down;
            const reachY = (velocityY - carried.y) / length - up.y * BRACE.down;
            const reach = Math.hypot(reachX, reachY) || 1;
            arms.forEach(side => {
                const anchor = this.joint(`shoulder_${side}_joint`).GetAnchorB();
                const extend = (this.armBones[side].upperLength + this.armBones[side].foreLength) * BRACE.reach[side];
                this.reachArm(side, {x: anchor.x + reachX / reach * extend, y: anchor.y + reachY / reach * extend}, 0, 180, BRACE.strength);
            });
            legs.forEach(side => {
                this.drive(`thigh_${side}_joint`, s * BRACE.hip * DEG, BRACE.strength);
                this.drive(`leg_${side}_joint`, s * BRACE.knee * DEG, BRACE.strength);
            });
            this.drive('head_joint', -s * BRACE.head, BRACE.strength);
        } else {
            this.releaseMotors();
        }
        if (!this.flopHeld && legs.length > 0 && down.time > RAGDOLL.minTime && down.still > RISE.settle) {
            const torso = c.torso.GetPosition();
            this.down = null;
            this.rise = {time: 0, angle: c.torso.GetAngle(), height: (torso.x - ground.x) * up.x + (torso.y - ground.y) * up.y};
            this.setControlled(true);
        }
    }
    // Something loose ahead of the hips too tall to step onto.
    pushAhead(hip, up, forward, height) {
        const above = height - HOVER.step - PUSH.clear;
        const hit = this.cast(hip.x - up.x * above, hip.y - up.y * above, forward.x, forward.y, PUSH.reach);
        if (!hit || hit.body.GetType() !== Box2D.b2_dynamicBody || hit.body.GetMass() <= 0) return null;
        return hit.nx * forward.x + hit.ny * forward.y < -MOVE.wallFacing ? hit : null;
    }
    // How much of a push against something loose it takes, by the two masses at the contact: a light thing gets little of
    // the runner's push, as it would move away under it.
    pushShare(ground, direction, mass) {
        const body = ground.body;
        if (body.GetType() !== Box2D.b2_dynamicBody || body.GetMass() <= 0) return 1;
        const center = body.GetWorldCenter();
        const local = body.GetLocalCenter();
        const inertia = body.GetInertia() - body.GetMass() * (local.x * local.x + local.y * local.y);
        const arm = (ground.x - center.x) * direction.y - (ground.y - center.y) * direction.x;
        const groundMass = 1 / (1 / body.GetMass() + (inertia > 0 ? arm * arm / inertia : 0));
        return groundMass / (groundMass + mass);
    }
    carriedVelocity(ground) {
        const carried = {x: 0, y: 0};
        if (ground.body.GetType() !== Box2D.b2_staticBody) {
            vec1.Set(ground.x, ground.y);
            const velocity = ground.body.GetLinearVelocityFromWorldPoint(vec1);
            carried.x = velocity.x;
            carried.y = velocity.y;
        }
        // A treadmill's belt carries along what stands on it.
        const belt = game.editor.retrieveClassFromBody(ground.body)?.beltVelocity?.(ground.nx, ground.ny);
        if (belt) {
            carried.x += belt.x;
            carried.y += belt.y;
        }
        return carried;
    }
    // Getting up: the feet plant under the hips while the torso lifts to standing height and turns upright, the
    // muscles coming back to full strength.
    updateRise(c) {
        const {parts, torso, up, right, upAngle, dt, ms, gx, gy, legs, arms, comX, comY, velocityX, velocityY, spinSpeed} = c;
        const rise = this.rise;
        const s = this.flipped ? -1 : 1;
        const ground = this.probe(comX, comY, up, RISE.ground);
        if (!ground) {
            this.knockDown();
            return;
        }
        rise.time += ms;
        const t = Math.min(1, rise.time / RISE.time);
        const k = smooth(t);
        this.strength = mix(RISE.strength, 1, k);
        const standTorso = this.reach * POSE.stand + this.ankleHeight + this.hipToTorso;
        const position = torso.GetPosition();
        const height = (position.x - ground.x) * up.x + (position.y - ground.y) * up.y;
        const target = mix(rise.height, standTorso, k);
        const lift = (standTorso - rise.height) * 6 * t * (1 - t) / (RISE.time / 1000 * dt / (ms / 1000));
        const carried = this.carriedVelocity(ground);
        const relativeX = velocityX - carried.x;
        const relativeY = velocityY - carried.y;
        const along = relativeX * right.x + relativeY * right.y;
        const upSpeed = relativeX * up.x + relativeY * up.y;
        let changeUp = lift + (target - height) * RISE.gain - upSpeed;
        let changeAlong = -along;
        const change = Math.hypot(changeUp, changeAlong);
        if (change > RISE.accel * dt) {
            changeUp *= RISE.accel * dt / change;
            changeAlong *= RISE.accel * dt / change;
        }
        this.push(parts, up.x * changeUp + right.x * changeAlong - gx * dt, up.y * changeUp + right.y * changeAlong - gy * dt);

        const turn = smooth(Math.min(1, t * RISE.turn));
        const targetAngle = rise.angle + wrapAngle(upAngle + s * POSE.lean - rise.angle) * turn;
        const error = wrapAngle(targetAngle - torso.GetAngle());
        this.spin(parts, (clamp(error * UPRIGHT.rate, -UPRIGHT.maxSpin, UPRIGHT.maxSpin) - spinSpeed) * UPRIGHT.response, comX, comY);
        this.pelvisAngle = torso.GetAngle() + error + s * this.waistRest;

        this.plantFeet(legs, up, right);
        arms.forEach(side => this.driveArm(side, AIR.rest));
        this.drive('head_joint', 0);
        if (t >= 1) {
            this.rise = null;
            this.strength = 1;
            this.airTime = 0;
            this.jumpsUsed = 0;
        }
    }
    // Feet on the ground under the hips, a little apart.
    plantFeet(legs, up, right) {
        const s = this.flipped ? -1 : 1;
        const forward = Math.atan2(right.y * s, right.x * s);
        legs.forEach(side => {
            const leg = this.legs[side];
            const hip = this.hipPoint(leg);
            const along = (side === 'left' ? GAIT.stance : -GAIT.stance) * s;
            const hit = this.probe(hip.x + right.x * along + up.x, hip.y + right.y * along + up.y, up, this.reach + 2);
            const normal = hit ? {x: hit.nx, y: hit.ny} : up;
            const point = hit ? {x: hit.x, y: hit.y} : {x: hip.x + right.x * along - up.x * this.reach, y: hip.y + right.y * along - up.y * this.reach};
            const footAngle = Math.atan2(normal.x, -normal.y);
            const foot = this.withinReach(leg, {x: point.x + normal.x * this.footSole, y: point.y + normal.y * this.footSole}, footAngle);
            this.driveLeg(leg, foot, footAngle, forward);
        });
    }
    flip() {
        // Mirroring tears off joints to anything outside the rig, a grip among them: let go properly first.
        this.letGoHold();
        game.editor.mirrorPrefab(this.character, Humanoid.BODY_PARTS.BODY);
        this.flipped = !this.flipped;
        // Mirroring negates every joint angle, and the poses with them.
        Object.values(this.trackState).forEach(state => state.target = -state.target);
        // Mirroring recomputes every body's mass.
        if (!this.released && !this.down) this.setPelvisInertia(true);
    }

    parts() {
        return this.character.lookupObject._bodies.filter(body => !body.snapped && !body.destroyed);
    }
    // What's left of a leg: whole, a stump without its foot, only the thigh, or nothing. A stump still stands.
    legState(side) {
        const rider = this.character.lookupObject;
        const attached = part => rider[`${part}_${side}`] && !rider[`${part}_${side}`].snapped && !rider[`${part}_${side}`].destroyed && this.joint(`${part}_${side}_joint`);
        if (!attached('thigh')) return 'lost';
        if (!attached('leg')) return 'thigh';
        return attached('feet') ? 'whole' : 'stump';
    }
    hasLeg(side) {
        const state = this.legState(side);
        return state === 'whole' || state === 'stump';
    }
    hasArm(side) {
        const rider = this.character.lookupObject;
        return ['shoulder', 'arm', 'hand'].every(part => rider[`${part}_${side}`] && !rider[`${part}_${side}`].snapped && this.joint(`${part}_${side}_joint`));
    }
    shoulderPoint(sides) {
        let x = 0;
        let y = 0;
        sides.forEach(side => {
            const anchor = this.joint(`shoulder_${side}_joint`).GetAnchorB();
            x += anchor.x / sides.length;
            y += anchor.y / sides.length;
        });
        return {x, y};
    }
    // How far joints are pulled apart: an arm hooked over something carries the body and tears off.
    armStrain(sides) {
        return this.strain(sides.flatMap(side => ['shoulder', 'arm', 'hand'].map(part => `${part}_${side}`)));
    }
    strain(parts) {
        let strain = 0;
        parts.forEach(part => {
            const joint = this.joint(`${part}_joint`);
            if (!joint) return;
            const a = joint.GetAnchorA();
            const ax = a.x;
            const ay = a.y;
            const b = joint.GetAnchorB();
            strain = Math.max(strain, Math.hypot(ax - b.x, ay - b.y));
        });
        return strain;
    }
    // Ray along -up from a point; ground is anything the character could stand on.
    probe(x, y, up, length) {
        return this.cast(x, y, -up.x, -up.y, length);
    }
    cast(x, y, directionX, directionY, length, fixedOnly = false) {
        const belly = this.character.lookupObject[Humanoid.BODY_PARTS.BELLY];
        const filter = belly.GetFixtureList().GetFilterData();
        const category = filter.categoryBits;
        const mask = filter.maskBits;
        vec1.Set(x, y);
        vec2.Set(x + directionX * length, y + directionY * length);
        const portals = this.openPortals();
        const hit = rayCastPortalAware(Box2D, belly.GetWorld(), belly, vec1, vec2, (fixture, point, normal, fraction, representation) => {
            const body = fixture.GetBody();
            if (fixture.IsSensor() || body.mainCharacter || body.isPortalSurface || this.heldBodies?.has(body)) return false;
            if (fixedOnly && body.GetType() === Box2D.b2_dynamicBody) return false;
            if (representation === 'primary' && portals.some(endpoint => this.inOpening(endpoint, fixture, point))) return false;
            const other = fixture.GetFilterData();
            return (other.maskBits & category) !== 0 && (other.categoryBits & mask) !== 0;
        });
        if (!hit) return null;
        return {x: hit.point.x, y: hit.point.y, nx: hit.normal.x, ny: hit.normal.y, body: hit.fixture.GetBody(), distance: hit.fraction * length};
    }
    openPortals() {
        if (this.portalsFrame === simulationFrame()) return this.portals;
        this.portalsFrame = simulationFrame();
        const portals = this.portals = [];
        const center = this.character.lookupObject[Humanoid.BODY_PARTS.BELLY].GetPosition();
        const aabb = new Box2D.b2AABB();
        aabb.get_lowerBound().Set(center.x - PORTAL.reach, center.y - PORTAL.reach);
        aabb.get_upperBound().Set(center.x + PORTAL.reach, center.y + PORTAL.reach);
        const query = Object.assign(new Box2D.JSQueryCallback(), {
            ReportFixture(fixturePointer) {
                const endpoint = Box2D.wrapPointer(fixturePointer, Box2D.b2Fixture).GetBody().portalEndpoint;
                if (endpoint?.bridge && endpoint.active !== false && !portals.includes(endpoint)) portals.push(endpoint);
                return true;
            },
        });
        this.character.lookupObject[Humanoid.BODY_PARTS.BELLY].GetWorld().QueryAABB(query, aabb);
        Box2D.destroy(query);
        Box2D.destroy(aabb);
        return portals;
    }
    inOpening(endpoint, fixture, point) {
        if (!endpointUsesHostFixture(Box2D, endpoint, fixture) || !pointWithinPortalAperture(point, endpoint)) return false;
        const position = endpoint.body.GetPosition();
        const normal = portalNormal(endpoint.body.GetAngle());
        return Math.abs((point.x - position.x) * normal.x + (point.y - position.y) * normal.y) < PORTAL.plane;
    }
    hipPoint(leg) {
        const belly = this.character.lookupObject[Humanoid.BODY_PARTS.BELLY];
        vec1.Set((this.flipped ? -1 : 1) * leg.hipOnBelly.x, leg.hipOnBelly.y);
        const hip = belly.GetWorldPoint(vec1);
        return {x: hip.x, y: hip.y};
    }
    ankleOffset(leg, footAngle) {
        // A stump puts the shin's end where the sole would be.
        if (leg.stump) {
            const down = this.footSole - CRIPPLE.clear;
            return {x: -Math.sin(footAngle) * down, y: Math.cos(footAngle) * down};
        }
        const x = (this.flipped ? -1 : 1) * leg.ankleOnFoot.x;
        const y = leg.ankleOnFoot.y;
        return {x: Math.cos(footAngle) * x - Math.sin(footAngle) * y, y: Math.sin(footAngle) * x + Math.cos(footAngle) * y};
    }
    toeOffset(footAngle) {
        const x = (this.flipped ? -1 : 1) * this.toe.x;
        const y = this.toe.y;
        return {x: Math.cos(footAngle) * x - Math.sin(footAngle) * y, y: Math.sin(footAngle) * x + Math.cos(footAngle) * y};
    }
    withinReach(leg, point, footAngle) {
        const hip = this.hipPoint(leg);
        const offset = this.ankleOffset(leg, footAngle);
        const x = point.x + offset.x - hip.x;
        const y = point.y + offset.y - hip.y;
        const reach = Math.hypot(x, y);
        const longest = this.reach * 0.97;
        if (reach <= longest) return point;
        return {x: hip.x + x * longest / reach - offset.x, y: hip.y + y * longest / reach - offset.y};
    }
    // The hips are posed against the pelvis angle the body is steered to: posed against the actual one, their push back
    // on the pelvis would move the target. A foot that would bend the ankle past its range tips onto its toes or heel, so
    // a deep squat lifts the heels.
    driveLeg(leg, foot, footAngle, forward) {
        let {thighAngle, shinAngle} = this.solveLeg(leg, foot, footAngle, forward);
        // Turned about the ball of the foot, which stays put; the heel and ankle come up, which bends the knee more.
        const toe = this.toeOffset(footAngle);
        for (let i = 0; i < 2 && !leg.stump; i++) {
            const ankle = wrapAngle(shinAngle - footAngle);
            if (Math.abs(ankle) <= FLEX.ankle - FLEX.ankleMargin) break;
            footAngle = shinAngle - Math.sign(ankle) * (FLEX.ankle - FLEX.ankleMargin);
            const turned = this.toeOffset(footAngle);
            ({thighAngle, shinAngle} = this.solveLeg(leg, {x: foot.x + toe.x - turned.x, y: foot.y + toe.y - turned.y}, footAngle, forward));
        }
        const give = this.compliance([`thigh_${leg.side}`, `leg_${leg.side}`, `feet_${leg.side}`]);
        this.drive(`thigh_${leg.side}_joint`, this.pelvisAngle - thighAngle, give);
        this.drive(`leg_${leg.side}_joint`, thighAngle - shinAngle, give);
        if (!leg.stump) this.drive(`feet_${leg.side}_joint`, shinAngle - footAngle, give);
    }
    // Two-bone IK from the hip to the ankle over the foot, knee bending towards forward.
    solveLeg(leg, foot, footAngle, forward) {
        const s = this.flipped ? -1 : 1;
        const hip = this.hipPoint(leg);
        const offset = this.ankleOffset(leg, footAngle);
        const ankleX = foot.x + offset.x;
        const ankleY = foot.y + offset.y;
        const thighLength = leg.thighLength;
        const shinLength = leg.shinLength;
        const reach = clamp(softReach(Math.hypot(ankleX - hip.x, ankleY - hip.y), thighLength + shinLength), Math.abs(thighLength - shinLength) + 0.01, thighLength + shinLength - 0.001);
        const direction = Math.atan2(ankleY - hip.y, ankleX - hip.x);
        const bend = Math.acos(clamp((thighLength * thighLength + reach * reach - shinLength * shinLength) / (2 * thighLength * reach), -1, 1));
        const thighDirection = Math.cos(direction - bend - forward) > Math.cos(direction + bend - forward) ? direction - bend : direction + bend;
        const kneeX = hip.x + thighLength * Math.cos(thighDirection);
        const kneeY = hip.y + thighLength * Math.sin(thighDirection);
        const shinDirection = Math.atan2(hip.y + reach * Math.sin(direction) - kneeY, hip.x + reach * Math.cos(direction) - kneeX);
        return {thighAngle: thighDirection - Math.atan2(leg.thigh.y, s * leg.thigh.x), shinAngle: shinDirection - Math.atan2(leg.shin.y, s * leg.shin.x)};
    }
    driveArms(arms, headPitch, strength = 1) {
        const s = this.flipped ? -1 : 1;
        SIDES.forEach(side => this.driveArm(side, arms[side], strength));
        this.drive('head_joint', -s * headPitch);
    }
    driveArm(side, [shoulder, elbow], strength = 1, tone = TONE.arm) {
        const s = this.flipped ? -1 : 1;
        strength = this.armStrength(side, strength);
        this.drive(`shoulder_${side}_joint`, s * shoulder * DEG, strength, tone);
        this.drive(`arm_${side}_joint`, s * elbow * DEG, strength, tone);
        this.drive(`hand_${side}_joint`, 0, strength * ARMS.hand, tone);
    }
    // An arm touching anything outside the body relaxes: a strong arm pressed on the world would shove the runner around.
    armStrength(side, strength) {
        return this.armTouching[side] ? Math.min(strength, MUSCLE.relaxed) : strength;
    }
    touching(parts) {
        const own = new Set(this.parts());
        return parts.some(part => {
            const body = this.character.lookupObject[part];
            if (!body || body.destroyed) return false;
            for (let edge = body.GetContactList(); Box2D.getPointer(edge) !== Box2D.getPointer(Box2D.NULL); edge = edge.get_next()) {
                const contact = edge.contact;
                if (contact.IsTouching() && contact.IsEnabled() && !own.has(edge.other) && !contact.GetFixtureA().IsSensor() && !contact.GetFixtureB().IsSensor()) return true;
            }
            return false;
        });
    }
    // Two-bone IK from the shoulder to the wrist. Elbows only bend one way, so of the two solutions the one within the
    // elbow's range is taken.
    // With a most bent elbow the arm only straightens towards a wrist too close for it, reaching past.
    reachArm(side, wrist, hand = 0, mostBent = 180, strength = 1) {
        const s = this.flipped ? -1 : 1;
        strength = this.armStrength(side, strength);
        const bones = this.armBones[side];
        const torso = this.character.lookupObject[Humanoid.BODY_PARTS.BODY];
        const anchor = this.joint(`shoulder_${side}_joint`).GetAnchorB();
        const shoulderX = anchor.x;
        const shoulderY = anchor.y;
        const upper = bones.upperLength;
        const fore = bones.foreLength;
        const shortest = Math.max(Math.abs(upper - fore) + 0.01, Math.sqrt(upper * upper + fore * fore + 2 * upper * fore * Math.cos(mostBent * DEG)));
        const reach = clamp(softReach(Math.hypot(wrist.x - shoulderX, wrist.y - shoulderY), upper + fore), shortest, upper + fore - 0.001);
        const direction = Math.atan2(wrist.y - shoulderY, wrist.x - shoulderX);
        const bend = Math.acos(clamp((upper * upper + reach * reach - fore * fore) / (2 * upper * reach), -1, 1));
        const solve = sign => {
            const upperDirection = direction + sign * bend;
            const elbowX = shoulderX + upper * Math.cos(upperDirection);
            const elbowY = shoulderY + upper * Math.sin(upperDirection);
            const foreDirection = Math.atan2(shoulderY + reach * Math.sin(direction) - elbowY, shoulderX + reach * Math.cos(direction) - elbowX);
            const upperAngle = upperDirection - Math.atan2(bones.upper.y, s * bones.upper.x);
            const foreAngle = foreDirection - Math.atan2(bones.fore.y, s * bones.fore.x);
            return {upperAngle, elbow: wrapAngle(upperAngle - foreAngle)};
        };
        const a = solve(1);
        const b = solve(-1);
        const {upperAngle, elbow} = s * a.elbow >= s * b.elbow ? a : b;
        const shoulderTarget = torso.GetAngle() - upperAngle;
        // Swinging a long way round the elbow folds, keeping the hand in close, and opens out as the shoulder arrives.
        const swing = Math.abs(wrapAngle(shoulderTarget - this.joint(`shoulder_${side}_joint`).GetJointAngle()));
        const fold = smooth(clamp((swing - ARMS.fold[0] * DEG) / ((ARMS.fold[1] - ARMS.fold[0]) * DEG), 0, 1));
        this.drive(`shoulder_${side}_joint`, shoulderTarget, strength);
        this.drive(`arm_${side}_joint`, s * Math.max(s * elbow, mix(s * elbow, ARMS.folded * DEG, fold)), strength);
        this.drive(`hand_${side}_joint`, hand, strength);
    }
    // Leg joints give way as they're pulled apart, so a leg caught on something yields instead of fighting it.
    compliance(parts) {
        return clamp(1 - (this.strain(parts) - COMPLY.start) / (COMPLY.end - COMPLY.start), COMPLY.min, 1);
    }
    // The head, torso or pelvis touching something that doesn't give way, in front of it.
    pressedAhead(forward) {
        const own = new Set(this.parts());
        const manifold = new Box2D.b2WorldManifold();
        const pressed = JAM.parts.some(part => {
            const body = this.character.lookupObject[part];
            if (!body || body.destroyed) return false;
            for (let edge = body.GetContactList(); Box2D.getPointer(edge) !== Box2D.getPointer(Box2D.NULL); edge = edge.get_next()) {
                const contact = edge.contact;
                if (!contact.IsTouching() || !contact.IsEnabled() || own.has(edge.other) || edge.other.GetType() === Box2D.b2_dynamicBody) continue;
                if (contact.GetFixtureA().IsSensor() || contact.GetFixtureB().IsSensor()) continue;
                contact.GetWorldManifold(manifold);
                // The manifold's normal points from fixture A to B.
                const sign = Box2D.getPointer(contact.GetFixtureA().GetBody()) === Box2D.getPointer(body) ? 1 : -1;
                const normal = manifold.get_normal();
                if (sign * (normal.x * forward.x + normal.y * forward.y) > JAM.facing) return true;
            }
            return false;
        });
        Box2D.destroy(manifold);
        return pressed;
    }
    // Something fixed over the runner, where its head would be standing up, is now (leaning in under it) or will be a moment
    // from now, too low to stand under. Loose things overhead are only passing.
    lowCeiling(hip, up, forward, hipHeight, speed, standHeight, margin) {
        const clearance = standHeight - hipHeight + this.hipToTorso + this.head.up + this.head.radius + margin;
        const head = this.character.lookupObject[Humanoid.BODY_PARTS.HEAD]?.GetPosition();
        const lean = head ? clamp((head.x - hip.x) * forward.x + (head.y - hip.y) * forward.y, 0, this.hipToTorso + this.head.up) : 0;
        const ahead = Math.max(this.head.forward + Math.max(0, speed) * CEILING.ahead, lean);
        return [-this.head.radius - margin, this.head.forward, ahead + this.head.radius + margin].some(along => {
            const hit = this.cast(hip.x + forward.x * along, hip.y + forward.y * along, up.x, up.y, clearance);
            return hit && hit.body.GetType() !== Box2D.b2_dynamicBody;
        });
    }
    // A top within reach above the shoulders, and the wall face just under its edge.
    findLedge(arms, up, forward) {
        const shoulder = this.shoulderPoint(arms);
        for (const along of LEDGE.probes) {
            const top = this.cast(shoulder.x + forward.x * along + up.x * LEDGE.reach, shoulder.y + forward.y * along + up.y * LEDGE.reach,
                -up.x, -up.y, LEDGE.reach + LEDGE.below);
            if (!top || top.nx * up.x + top.ny * up.y < 0.7) continue;
            const height = LEDGE.reach - top.distance - 0.15;
            const face = this.cast(shoulder.x - forward.x * 0.4 + up.x * height, shoulder.y - forward.y * 0.4 + up.y * height,
                forward.x, forward.y, along + 0.4);
            if (!face || face.body !== top.body || face.nx * forward.x + face.ny * forward.y > -0.7) continue;
            if (this.slender(top.body, forward)) continue;
            vec1.Set(face.x + up.x * 0.15, face.y + up.y * 0.15);
            const local = top.body.GetLocalPoint(vec1);
            return {body: top.body, local: {x: local.x, y: local.y}, corner: {x: vec1.x, y: vec1.y}, height: height + 0.15, climb: -1};
        }
        return null;
    }
    // Holding up while catching a ledge climbs it straight away; otherwise the runner hangs.
    grabLedge(ledge, torso, up, forward) {
        this.ledge = ledge;
        this.sound(SOUND.grab, 0.25, torso.GetPosition());
        const position = torso.GetPosition();
        const offsetX = position.x - ledge.corner.x;
        const offsetY = position.y - ledge.corner.y;
        // With the head up over the top the body can stay as close as keeps it off the edge; hanging lower, the arms and
        // knees need the full distance.
        ledge.depth = clamp(-(offsetX * up.x + offsetY * up.y), LEDGE.highDepth, LEDGE.depth);
        ledge.back = clamp(-(offsetX * forward.x + offsetY * forward.y), this.headClearance(ledge.depth), LEDGE.back);
        // A top below the shoulders can't be hung from: the runner is already above it and vaults on.
        ledge.vault = ledge.height < 0;
        if (this.vertical > 0 || ledge.vault) {
            ledge.climb = 0;
            ledge.start = {x: offsetX, y: offsetY};
        }
        this.kicked = null;
        this.jumpsUsed = 0;
        this.jumpBuffer = 0;
        this.jumpRise = null;
        this.flipTime = -1;
        this.landTime = -1;
        this.apex = null;
    }
    headClearance(depth) {
        const headAbove = this.head.up - depth;
        const clear = this.head.forward + (headAbove > 0 ? Math.sqrt(Math.max(0, (this.head.radius + LEDGE.headGap) ** 2 - headAbove * headAbove)) : this.head.radius + LEDGE.headGap);
        return Math.max(LEDGE.minBack, clear);
    }
    letGo(jumpsUsed, cooldown = LEDGE.cooldown) {
        this.ledge = null;
        this.grabCooldown = cooldown;
        this.jumpsUsed = jumpsUsed;
        this.airTime = JUMP.coyote + 1;
    }
    // Hanging holds the torso under the edge; climbing carries it up past the edge and then onto the top.
    updateLedge(c) {
        const {parts, torso, up, right, upAngle, dt, ms, gx, gy, mass, legs, arms, move} = c;
        const s = this.flipped ? -1 : 1;
        const ledge = this.ledge;
        const body = ledge.body;
        const forward = {x: right.x * s, y: right.y * s};
        // Joints pulled apart for 30 frames tear; a body that stays stretched lets go first.
        ledge.strained = this.strain(LIMBS) > LEDGE.tear ? (ledge.strained ?? 0) + 1 : 0;
        // Hanging with the floor within standing height is standing.
        const hip = this.hipPoint(this.legs[legs[0]]);
        const floor = ledge.climb < 0 && this.probe(hip.x, hip.y, up, this.reach * POSE.stand + this.ankleHeight);
        if (body.destroyed || arms.length === 0 || ledge.strained > LEDGE.tearFrames || floor) {
            this.letGo(1, LEDGE.drop);
            return;
        }
        vec1.Set(ledge.local.x, ledge.local.y);
        const cornerPoint = body.GetWorldPoint(vec1);
        const corner = {x: cornerPoint.x, y: cornerPoint.y};
        const carriedPoint = body.GetLinearVelocityFromWorldPoint(vec1);
        const carried = {x: carriedPoint.x, y: carriedPoint.y};
        const at = (along, height) => ({x: corner.x + forward.x * along + up.x * height, y: corner.y + forward.y * along + up.y * height});
        const position = torso.GetPosition();
        const torsoX = position.x;
        const torsoY = position.y;

        const pressedUp = this.vertical > 0 && this.lastVertical <= 0;
        const pressedDown = this.vertical < 0 && this.lastVertical >= 0;
        if (ledge.climb < 0) {
            if (this.jumpBuffer > 0 && move === -s) {
                this.letGo(1);
                this.character.flip();
                const speed = c.velocityX * up.x + c.velocityY * up.y;
                this.push(parts, -forward.x * LEAP.away + up.x * (LEAP.up - speed), -forward.y * LEAP.away + up.y * (LEAP.up - speed));
                this.jumpIgnore = JUMP.groundIgnore;
                this.jumpRise = 'held';
                this.jumpBuffer = 0;
                return;
            }
            if (pressedUp || this.jumpBuffer > 0) {
                ledge.climb = 0;
                ledge.start = {x: torsoX - corner.x, y: torsoY - corner.y};
                this.jumpBuffer = 0;
            } else if (pressedDown) {
                this.letGo(1, LEDGE.drop);
                return;
            }
        }

        let target;
        let pathX = 0;
        let pathY = 0;
        let lean = LEDGE.lean;
        let pose = AIR.hang;
        let pull = true;
        if (ledge.climb < 0) {
            // After a high catch the body settles down to arm's length once the hands are at the edge, and in against the
            // wall as far as keeps the head off it once the arms are up over it: an arm still down in front would be
            // trapped against the wall.
            const handsAtEdge = arms.every(side => {
                const hand = this.character.lookupObject[`hand_${side}`].GetPosition();
                return (hand.x - corner.x) * forward.x + (hand.y - corner.y) * forward.y < LEDGE.settleReach;
            });
            if (handsAtEdge) {
                ledge.depth = Math.min(LEDGE.depth, ledge.depth + LEDGE.settleRate * dt);
                const back = ledge.overhead ? this.headClearance(ledge.depth) : Math.max(ledge.back, this.headClearance(ledge.depth));
                ledge.back += clamp(back - ledge.back, -LEDGE.settleRate * dt, LEDGE.settleRate * dt);
            }
            target = at(-ledge.back, -ledge.depth);
        } else {
            const hipToTorso = (torsoX - hip.x) * up.x + (torsoY - hip.y) * up.y;
            // Up past the edge clear of the wall, or for a vault straight up from where the runner is.
            const startAlong = ledge.start.x * forward.x + ledge.start.y * forward.y;
            const startHeight = ledge.start.x * up.x + ledge.start.y * up.y;
            const over = ledge.vault ? at(startAlong, Math.max(startHeight, CLIMB.over + hipToTorso)) : at(-CLIMB.back, CLIMB.over + hipToTorso);
            const onto = at(CLIMB.onto, this.reach * POSE.stand + this.ankleHeight + hipToTorso);
            // A vault is a quick hop forward onto the top; a climb pulls up first.
            const duration = ledge.vault ? CLIMB.vaultTime : CLIMB.time;
            const split = ledge.vault ? CLIMB.vaultSplit : CLIMB.split;
            const path = time => {
                const t = clamp(time / duration, 0, 1);
                if (t < split) {
                    const k = smooth(t / split);
                    return {x: mix(corner.x + ledge.start.x, over.x, k), y: mix(corner.y + ledge.start.y, over.y, k)};
                }
                const k = smooth((t - split) / (1 - split));
                return {x: mix(over.x, onto.x, k), y: mix(over.y, onto.y, k)};
            };
            target = path(ledge.climb);
            const next = path(ledge.climb + ms);
            pathX = (next.x - target.x) / dt;
            pathY = (next.y - target.y) / dt;
            ledge.climb += ms;
            lean = CLIMB.lean;
            // A vault keeps the legs tucked all the way over the edge, the hands pushing off the top.
            const stepping = ledge.climb / duration >= split;
            pose = ledge.vault ? AIR.vault : stepping ? AIR.step : AIR.pull;
            pull = !stepping && !ledge.vault;
            if (Math.hypot(target.x - torsoX, target.y - torsoY) > CLIMB.abort) {
                this.letGo(1);
                return;
            }
            if (ledge.climb >= duration) {
                this.ledge = null;
                this.jumpsUsed = 0;
                this.airTime = 0;
                this.grounded = true;
            }
        }

        // Follow the target, closing in on it at a capped speed (gently into the hang), and take the ledge's own motion along.
        const offset = Math.hypot(target.x - torsoX, target.y - torsoY);
        const catchUp = Math.min(LEDGE.gain, (ledge.climb < 0 ? LEDGE.settle : LEDGE.maxSpeed) / Math.max(offset, 0.001));
        let changeX = carried.x + pathX + (target.x - torsoX) * catchUp - c.velocityX;
        let changeY = carried.y + pathY + (target.y - torsoY) * catchUp - c.velocityY;
        const change = Math.hypot(changeX, changeY);
        if (change > LEDGE.accel * dt) {
            changeX *= LEDGE.accel * dt / change;
            changeY *= LEDGE.accel * dt / change;
        }
        const applyX = changeX - gx * dt;
        const applyY = changeY - gy * dt;
        this.push(parts, applyX, applyY);
        if (body.GetType() === Box2D.b2_dynamicBody) {
            vec1.Set(-mass * applyX / dt, -mass * applyY / dt);
            vec2.Set(corner.x, corner.y);
            body.ApplyForce(vec1, vec2, true);
        }

        const error = wrapAngle(upAngle + s * lean - torso.GetAngle());
        this.spin(parts, (clamp(error * UPRIGHT.rate, -UPRIGHT.maxSpin, UPRIGHT.maxSpin) - c.spinSpeed) * UPRIGHT.response, c.comX, c.comY);
        this.pelvisAngle = torso.GetAngle() + error + s * this.waistRest;

        this.poseLegs(legs, torso, pose);
        if (pull) {
            // An arm caught down between the body and the wall can't swing up through it and hangs limp. With no arm
            // up on the ledge for a moment the runner lets go rather than hang from nothing.
            // The arms reach for the lip, hardly bent, so the hands hold it, bent over it, one a little further in so both
            // show. Caught high, the arms are longer than the way to the lip and lie over the top, and the hands slide back
            // to it as the body sinks: an arm bent to put its hand on the lip would fold its elbow into the corner.
            const shoulder = this.shoulderPoint(arms);
            const lip = smooth(clamp((-((shoulder.x - corner.x) * up.x + (shoulder.y - corner.y) * up.y) - LEDGE.lip[0]) / (LEDGE.lip[1] - LEDGE.lip[0]), 0, 1));
            // An arm grips with its hand at or over the edge, or is on its way up to it.
            const atLip = side => {
                const hand = this.character.lookupObject[`hand_${side}`].GetPosition();
                const handX = hand.x - corner.x;
                const handY = hand.y - corner.y;
                return handX * forward.x + handY * forward.y > -LEDGE.handReach && handX * up.x + handY * up.y > -LEDGE.handReach;
            };
            const raised = arms.filter(side => atLip(side) || s * this.joint(`shoulder_${side}_joint`).GetJointAngle() > LEDGE.raised * DEG);
            // An arm that didn't get up while hanging swings up to the lip in front of the wall, not colliding on the way:
            // it couldn't come up past the wall otherwise.
            arms.forEach(side => {
                const limb = CAUGHT[SIDES.indexOf(side)].map(part => this.character.lookupObject[part]).filter(body => body && !body.destroyed);
                if (!raised.includes(side) && ledge.climb < 0) limb.forEach(body => !body.slipping && this.setSlipping(body, true));
                else if (atLip(side)) limb.forEach(body => body.slipping && this.setSlipping(body, false));
                this.reachArm(side, at(LEDGE.grip[side], LEDGE.gripAbove), -s * LEDGE.curl * lip * DEG, LEDGE.elbow);
            });
            this.drive('head_joint', 0);
            ledge.overhead = raised.length > 0 && raised.every(side => s * this.joint(`shoulder_${side}_joint`).GetJointAngle() > LEDGE.overhead * DEG);
            ledge.ungripped = raised.length === 0 && ledge.climb < 0 ? (ledge.ungripped ?? 0) + 1 : 0;
            if (ledge.ungripped > LEDGE.gripFrames) this.letGo(1, LEDGE.drop);
        } else {
            this.driveArms(pose.arms, 0);
        }
    }
    // A wall faced at chest height that the runner can slide down or kick off.
    findWall(directionX, directionY, length) {
        const chest = this.character.lookupObject[Humanoid.BODY_PARTS.BODY].GetPosition();
        const hit = this.cast(chest.x, chest.y, directionX, directionY, length);
        if (!hit || hit.body.GetType() === Box2D.b2_dynamicBody || hit.nx * directionX + hit.ny * directionY > -MOVE.wallFacing) return null;
        return hit;
    }
    // Sliding down a wall while steering into it. Returns whether it handled the frame.
    updateWall(c) {
        const {parts, torso, up, upAngle, dt, gx, gy, legs, arms, move, velocityX, velocityY, spinSpeed, comX, comY} = c;
        const s = this.flipped ? -1 : 1;
        const forward = {x: c.right.x * s, y: c.right.y * s};
        const wall = legs.length === 2 && this.findWall(forward.x, forward.y, this.wall ? SLIDE.keep : SLIDE.catch);
        const fall = -(velocityX * up.x + velocityY * up.y);
        if (!this.wall) {
            if (!wall || move !== s || this.vertical < 0 || fall < SLIDE.start || this.flipTime >= 0) return false;
            this.wall = {strained: 0};
            this.jumpRise = null;
        }
        this.wall.strained = this.strain(LIMBS) > LEDGE.tear ? this.wall.strained + 1 : 0;
        if (!wall || move !== s || this.vertical < 0 || this.wall.strained > SLIDE.tearFrames) {
            this.leaveWall();
            return false;
        }
        const length = Math.hypot(wall.nx, wall.ny);
        const normal = {x: wall.nx / length, y: wall.ny / length};
        this.wall.normal = normal;
        this.apex = comX * up.x + comY * up.y;
        this.wall.point = {x: wall.x, y: wall.y};
        if (this.jumpBuffer > 0 && !this.climbing(c, normal, wall)) {
            this.kick(c, {normal, x: wall.x, y: wall.y});
            return true;
        }
        // Down the wall, which needn't be square to gravity.
        const along = up.x * normal.x + up.y * normal.y;
        const tangentLength = Math.hypot(up.x - normal.x * along, up.y - normal.y * along);
        const tangent = {x: (up.x - normal.x * along) / tangentLength, y: (up.y - normal.y * along) / tangentLength};
        const carried = this.carriedVelocity(wall);
        const relativeX = velocityX - carried.x;
        const relativeY = velocityY - carried.y;
        const chest = torso.GetPosition();
        const away = (chest.x - wall.x) * normal.x + (chest.y - wall.y) * normal.y;
        let changeOut = (SLIDE.standOff - away) * SLIDE.gain - (relativeX * normal.x + relativeY * normal.y);
        changeOut = clamp(changeOut, -SLIDE.accel * dt, SLIDE.accel * dt);
        const sliding = -(relativeX * tangent.x + relativeY * tangent.y);
        const changeDown = sliding > SLIDE.speed ? -Math.min(sliding - SLIDE.speed, SLIDE.brake * dt) : Math.min(SLIDE.speed - sliding, SLIDE.slip * dt);
        this.push(parts, normal.x * changeOut - tangent.x * changeDown - gx * dt, normal.y * changeOut - tangent.y * changeDown - gy * dt);

        const error = wrapAngle(upAngle + s * SLIDE.lean - torso.GetAngle());
        this.spin(parts, (clamp(error * UPRIGHT.airRate, -UPRIGHT.maxSpin, UPRIGHT.maxSpin) - spinSpeed) * UPRIGHT.response, comX, comY);
        this.pelvisAngle = torso.GetAngle() + error + s * this.waistRest;

        // The lead foot on the wall below the hips, the other hanging back; the hands on the wall, one reaching higher.
        const onWall = (point, gap) => {
            const off = (point.x - wall.x) * normal.x + (point.y - wall.y) * normal.y;
            return {x: point.x - normal.x * (off - gap), y: point.y - normal.y * (off - gap)};
        };
        const angle = torso.GetAngle();
        const footAngle = angle + s * 0.15;
        const torsoForward = {x: Math.cos(angle) * s, y: Math.sin(angle) * s};
        legs.forEach(side => {
            const leg = this.legs[side];
            const hip = this.hipPoint(leg);
            const point = side === 'left' ? onWall({x: hip.x - tangent.x * SLIDE.foot, y: hip.y - tangent.y * SLIDE.foot}, SLIDE.footGap)
                : {x: hip.x + torsoForward.x * SLIDE.backFoot.x - tangent.x * SLIDE.backFoot.y, y: hip.y + torsoForward.y * SLIDE.backFoot.x - tangent.y * SLIDE.backFoot.y};
            this.driveLeg(leg, this.withinReach(leg, point, footAngle), footAngle, Math.atan2(torsoForward.y, torsoForward.x));
        });
        arms.forEach(side => {
            const anchor = this.joint(`shoulder_${side}_joint`).GetAnchorB();
            const shoulder = {x: anchor.x, y: anchor.y};
            // Hands rest on the wall lightly: pressed hard they'd push the body off it.
            this.reachArm(side, onWall({x: shoulder.x + tangent.x * SLIDE.hands[side], y: shoulder.y + tangent.y * SLIDE.hands[side]}, SLIDE.handGap), 0, 180, MUSCLE.relaxed);
        });
        this.drive('head_joint', 0);
        return true;
    }
    leaveWall() {
        if (this.wall?.normal) this.lastWall = {normal: this.wall.normal, ...this.wall.point, time: KICK.grace};
        this.wall = null;
    }
    // A wall to kick off in the air: one just left, one at the back, or one in front while not steering into it.
    kickWall(c, move) {
        const s = this.flipped ? -1 : 1;
        const face = hit => {
            if (!hit) return null;
            const length = Math.hypot(hit.nx, hit.ny);
            const normal = {x: hit.nx / length, y: hit.ny / length};
            return this.climbing(c, normal, hit) ? null : {normal, x: hit.x, y: hit.y};
        };
        if (this.lastWall) return face({nx: this.lastWall.normal.x, ny: this.lastWall.normal.y, x: this.lastWall.x, y: this.lastWall.y});
        return face(this.findWall(-c.right.x * s, -c.right.y * s, KICK.reach)) || (move !== s && face(this.findWall(c.right.x * s, c.right.y * s, KICK.reach)));
    }
    // Kicking off the same face again from as high as last time or higher would climb a single wall.
    climbing(c, normal, point) {
        const last = this.kicked;
        return Boolean(last) && c.comX * c.up.x + c.comY * c.up.y >= last.height && normal.x * last.normal.x + normal.y * last.normal.y > KICK.same &&
            Math.abs((point.x - last.x) * last.normal.x + (point.y - last.y) * last.normal.y) < KICK.plane;
    }
    // Kicking off a wall turns the runner away from it and launches it out and up. The air jump comes back only on landing:
    // a kick doesn't refund one already spent, or kicking off a wall and jumping back to it would climb it forever.
    kick(c, wall) {
        const {parts, up, velocityX, velocityY} = c;
        const normal = wall.normal;
        this.kicked = {normal, x: wall.x, y: wall.y, height: c.comX * up.x + c.comY * up.y};
        const s = this.flipped ? -1 : 1;
        if (c.right.x * s * normal.x + c.right.y * s * normal.y < 0) this.character.flip();
        const out = velocityX * normal.x + velocityY * normal.y;
        const rise = velocityX * up.x + velocityY * up.y;
        this.push(parts, normal.x * (KICK.away - out) + up.x * (KICK.up - rise), normal.y * (KICK.away - out) + up.y * (KICK.up - rise));
        this.sound(SOUND.kick, 0.3, c.torso.GetPosition());
        this.sound(SOUND.jump, 0.2, c.torso.GetPosition());
        this.wall = null;
        this.lastWall = null;
        this.kickLock = KICK.lock;
        this.holdTime = 0;
        this.jumpsUsed = Math.max(this.jumpsUsed, 1);
        this.jumpBuffer = 0;
        this.jumpIgnore = JUMP.groundIgnore;
        this.jumpRise = this.jumpHeld ? 'held' : 'cut';
        this.airTime = JUMP.coyote + 1;
        this.flipTime = -1;
        this.landTime = -1;
    }
    startRoll(c, hip, air = false) {
        const {parts, torso, up, right, comX, comY, velocityX, velocityY} = c;
        const s = this.flipped ? -1 : 1;
        // Part of the fall goes on into the roll.
        const fall = Math.max(0, -(velocityX * up.x + velocityY * up.y));
        this.sound(SOUND.roll, 0.25, torso.GetPosition());
        const along = velocityX * right.x * s + velocityY * right.y * s;
        this.roll = {time: 0, air, absorb: air ? ROLL.absorb : 0, angle: torso.GetAngle(), speed: clamp(Math.max(along, fall * ROLL.carry), ROLL.speed, MOVE.run),
            endHeight: this.reach * ROLL.crouch + this.ankleHeight + (comX - hip.x) * up.x + (comY - hip.y) * up.y};
        this.landTime = -1;
        this.flipTime = -1;
        this.jumpRise = null;
        this.jumpsUsed = 0;
        this.airTime = 0;
        this.apex = null;
        this.rollBuffer = 0;
    }
    // Rolling along the ground: the centre held low while the body turns a full turn forwards and comes upright. Returns
    // whether it handled the frame; off an edge, or jumping near the end, the runner carries on normally.
    updateRoll(c) {
        const {parts, torso, up, right, upAngle, dt, ms, gx, gy, legs, arms, comX, comY, velocityX, velocityY, spinSpeed} = c;
        const s = this.flipped ? -1 : 1;
        const roll = this.roll;
        const ground = this.probe(comX, comY, up, roll.air ? ROLL.lookAhead : ROLL.probe);
        const t = Math.min(1, (roll.time + ms) / ROLL.time);
        if (!ground || (this.jumpBuffer > 0 && t > ROLL.jumpAfter)) {
            this.roll = null;
            return false;
        }
        const forward = {x: right.x * s, y: right.y * s};
        const carried = this.carriedVelocity(ground);
        const relativeX = velocityX - carried.x;
        const relativeY = velocityY - carried.y;
        const height = (comX - ground.x) * up.x + (comY - ground.y) * up.y;
        const depth = this.depthBelow(parts, up, comX, comY);
        if (roll.air && ground.distance - depth < ROLL.contact) roll.air = false;
        if (roll.absorb < ROLL.absorb) {
            this.absorbRoll(c, ground, forward, relativeX, relativeY, height);
            return true;
        }
        if (!roll.air || t < ROLL.airHold) roll.time += ms;
        // The body is long and stiff, so it rolls over its outline: the centre rises as the shoulders and head pass under it.
        // On the ground the fall is caught hard, the legs and back taking it as the body turns.
        if (!roll.air) {
            const unroll = smooth(clamp((t - ROLL.rise) / (1 - ROLL.rise), 0, 1));
            const target = mix(depth + ROLL.clear, roll.endHeight, unroll);
            const lift = clamp(HOVER.stiffness * (target - height) - HOVER.damping * (relativeX * up.x + relativeY * up.y), -HOVER.maxAccel, ROLL.catch);
            // Rolling into a wall stops it.
            const wall = this.findWall(forward.x, forward.y, ROLL.wall);
            const speed = wall ? 0 : roll.speed;
            const along = relativeX * forward.x + relativeY * forward.y;
            const change = clamp(speed - along, -ROLL.accel * dt, ROLL.accel * dt);
            this.push(parts, (up.x * lift - gx) * dt + forward.x * change, (up.y * lift - gy) * dt + forward.y * change);
            if (ground.body.GetType() === Box2D.b2_dynamicBody) {
                const spring = lift * this.pushShare(ground, up, c.mass);
                vec1.Set(-c.mass * up.x * spring, -c.mass * up.y * spring);
                vec2.Set(ground.x, ground.y);
                ground.body.ApplyForce(vec1, vec2, true);
            }
        }

        // A full turn forwards, ending upright whatever angle it started at.
        // The turn is done by the end of turnTime, so the body comes up over feet already under it.
        const turnT = Math.min(1, t / ROLL.turnTime);
        const turn = s * Math.PI * 2 + wrapAngle(upAngle + s * (POSE.lean + POSE.crouchLean) - roll.angle);
        const error = roll.angle + turn * smooth(turnT) - torso.GetAngle();
        const spinTarget = error * UPRIGHT.flipRate + turn * 6 * turnT * (1 - turnT) / (ROLL.time * ROLL.turnTime / 1000 * dt / (ms / 1000));
        this.spin(parts, (clamp(spinTarget, -UPRIGHT.maxSpin * 2, UPRIGHT.maxSpin * 2) - spinSpeed) * UPRIGHT.response, comX, comY);
        this.pelvisAngle = torso.GetAngle() + error + s * this.waistRest;
        const plant = smooth(clamp((t - ROLL.plant[0]) / (ROLL.plant[1] - ROLL.plant[0]), 0, 1));
        if (t >= ROLL.plant[1]) this.plantFeet(legs, up, right);
        else this.poseLegs(legs, torso, {front: mixPoint(ROLL.tuck.front, ROLL.under.front, plant), back: mixPoint(ROLL.tuck.back, ROLL.under.back, plant)});
        const hands = 1 - smooth(clamp(t / ROLL.hands, 0, 1));
        arms.forEach(side => this.driveArm(side, ROLL.tuck.arms[side].map((angle, i) => mix(mix(angle, ROLL.reach[side][i], hands), ROLL.out[side][i], plant))));
        this.drive('head_joint', -s * ROLL.head * (1 - plant));
        if (t >= 1) {
            this.roll = null;
            this.grounded = true;
            this.airTime = 0;
            // It comes out running at the speed it rolled.
            this.gaitSpeed = roll.speed;
            this.moving = 1;
        }
        return true;
    }
    // The landing before the roll: feet on the ground, the legs sink into a crouch braking the fall, the body tips forward
    // and the hands go out for the ground. The turn starts from wherever the body has tipped to.
    absorbRoll(c, ground, forward, relativeX, relativeY, height) {
        const {parts, torso, up, right, upAngle, dt, ms, gx, gy, legs, arms, comX, comY, spinSpeed} = c;
        const s = this.flipped ? -1 : 1;
        const roll = this.roll;
        roll.absorb += ms;
        const k = smooth(Math.min(1, roll.absorb / ROLL.absorb));
        const upSpeed = relativeX * up.x + relativeY * up.y;
        const lift = clamp(HOVER.stiffness * (roll.endHeight - height) - HOVER.damping * upSpeed, -HOVER.maxAccel, ROLL.catch);
        const along = relativeX * forward.x + relativeY * forward.y;
        const change = clamp(roll.speed - along, -ROLL.accel * dt, ROLL.accel * dt);
        this.push(parts, (up.x * lift - gx) * dt + forward.x * change, (up.y * lift - gy) * dt + forward.y * change);
        if (ground.body.GetType() === Box2D.b2_dynamicBody) {
            const spring = lift * this.pushShare(ground, up, c.mass);
            vec1.Set(-c.mass * up.x * spring, -c.mass * up.y * spring);
            vec2.Set(ground.x, ground.y);
            ground.body.ApplyForce(vec1, vec2, true);
        }
        const error = wrapAngle(upAngle + s * (POSE.lean + ROLL.pitch * k) - torso.GetAngle());
        this.spin(parts, (clamp(error * UPRIGHT.flipRate, -UPRIGHT.maxSpin, UPRIGHT.maxSpin) - spinSpeed) * UPRIGHT.response, comX, comY);
        this.pelvisAngle = torso.GetAngle() + error + s * this.waistRest;
        this.plantFeet(legs, up, right);
        arms.forEach(side => this.driveArm(side, ROLL.reach[side]));
        this.drive('head_joint', -s * ROLL.head * k);
        if (roll.absorb >= ROLL.absorb) roll.angle = torso.GetAngle();
    }
    // How far the body reaches below its centre of mass.
    depthBelow(parts, up, comX, comY) {
        let depth = 0;
        parts.forEach(body => {
            for (let fixture = body.GetFixtureList(); Box2D.getPointer(fixture) !== Box2D.getPointer(Box2D.NULL); fixture = fixture.GetNext()) {
                if (fixture.IsSensor()) continue;
                const shape = fixture.GetShape();
                if (shape.GetType() === Box2D.b2Shape.e_circle) {
                    const circle = Box2D.castObject(shape, Box2D.b2CircleShape);
                    const center = body.GetWorldPoint(circle.get_m_p());
                    depth = Math.max(depth, (comX - center.x) * up.x + (comY - center.y) * up.y + circle.get_m_radius());
                } else if (shape.GetType() === Box2D.b2Shape.e_polygon) {
                    const polygon = Box2D.castObject(shape, Box2D.b2PolygonShape);
                    for (let i = 0; i < polygon.get_m_count(); i++) {
                        const point = body.GetWorldPoint(polygon.get_m_vertices(i));
                        depth = Math.max(depth, (comX - point.x) * up.x + (comY - point.y) * up.y);
                    }
                }
            }
        });
        return depth;
    }
    // Velocity changes that every part shares, as if the body were rigid, so they never strain a joint.
    push(parts, dx, dy) {
        if (this.track) {
            this.track.pushedX += dx;
            this.track.pushedY += dy;
        }
        parts.forEach(body => {
            const velocity = body.GetLinearVelocity();
            vec1.Set(velocity.x + dx, velocity.y + dy);
            body.SetLinearVelocity(vec1);
        });
    }
    spin(parts, change, centerX, centerY) {
        parts.forEach(body => {
            const center = body.GetWorldCenter();
            const velocity = body.GetLinearVelocity();
            vec1.Set(velocity.x - change * (center.y - centerY), velocity.y + change * (center.x - centerX));
            body.SetLinearVelocity(vec1);
            body.SetAngularVelocity(body.GetAngularVelocity() + change);
        });
    }

    update() {
        super.update();
        if (this.released || !this.legs) return;
        if (simulationFrame() === this.rigFrame) return;
        this.rigFrame = simulationFrame();
        this.pose = {};
        this.control();
        if (this.released) return;
        if (!this.down) {
            const s = this.flipped ? -1 : 1;
            this.drive(`${Humanoid.BODY_PARTS.BELLY}_joint`, s * this.waistRest, 1, TONE.firm);
            // A thigh without its shin hangs a little forward, loosely.
            SIDES.forEach(side => this.legState(side) === 'thigh' && this.drive(`thigh_${side}_joint`, s * CRIPPLE.thigh * DEG, CRIPPLE.thighStrength, TONE.loose));
        }
        this.followPose();
    }
    control() {

        const rider = this.character.lookupObject;
        const torso = rider[Humanoid.BODY_PARTS.BODY];
        const belly = rider[Humanoid.BODY_PARTS.BELLY];
        const parts = this.parts();
        if (!this.character.alive || !torso || !belly || !parts.includes(torso) || !parts.includes(belly)) {
            this.release();
            return;
        }
        const legs = SIDES.filter(side => this.hasLeg(side));
        SIDES.forEach(side => this.legs[side].stump = this.legState(side) === 'stump');
        if (legs.length === 0) {
            this.release();
            return;
        }
        // Carrying something, up and down raise and lower it, to swing it at someone, rather than climb or crouch.
        this.clearBrokenGrips();
        const carrying = Boolean(this.grabHeld) && this.holding() && !this.anchored;
        this.updateHeldCollision();
        this.vertical = carrying ? 0 : this.verticalInput;
        // Resting on something, it isn't pushed further into it: that push would go back up the arms into the body.
        this.carryTouch = carrying ? this.heldTouch() : null;
        const aimInput = this.verticalInput < 0 && this.carryTouch?.below || this.verticalInput > 0 && this.carryTouch?.above ? 0 : this.verticalInput;
        this.aim = carrying ? clamp((this.aim ?? 0) + aimInput * GRAB.aim * this.stepTime, -1, 1) : 0;
        // What's left of a lost limb hangs loose.
        SIDES.forEach(side => {
            if (!legs.includes(side)) ['thigh', 'leg', 'feet'].forEach(part => this.joint(`${part}_${side}_joint`)?.EnableMotor(false));
            if (!this.hasArm(side)) ['shoulder', 'arm', 'hand'].forEach(part => this.joint(`${part}_${side}_joint`)?.EnableMotor(false));
        });

        const dt = Settings.physicsTimeStep * game.editor.editorSettingsObject.gameSpeed;
        const ms = game.editor.deltaTime;
        this.stepTime = dt;
        const gravity = torso.GetWorld().GetGravity();
        const gx = gravity.x;
        const gy = gravity.y;
        const gLength = Math.hypot(gx, gy);
        const up = gLength > 0.001 ? {x: -gx / gLength, y: -gy / gLength} : {x: 0, y: -1};
        const right = {x: -up.y, y: up.x};
        const upAngle = Math.atan2(up.x, -up.y);

        // Left and right stay the screen's: with gravity turned past sideways the runner's own right points left.
        if (right.x < -CONTROLS.mirror) this.mirroredControls = true;
        else if (right.x > CONTROLS.mirror) this.mirroredControls = false;
        // The helicopter hat lifts the runner off the ground; on the rope hat's rope it swings, and turning would let go.
        const hat = this.character.hat;
        const flying = hat?.boosting > 0;
        const roped = Boolean(hat?.isRopeHat && hat.blockControls);
        // Turning mirrors the whole rig, so it happens before anything reads a side.
        this.kickLock = Math.max(0, this.kickLock - ms);
        // Just after a kick off a wall the runner keeps going the way it was kicked.
        const move = this.kickLock > 0 ? (this.flipped ? -1 : 1) : this.moveFrame === simulationFrame() ? this.moveDir * (this.mirroredControls ? -1 : 1) : 0;
        // Hanging on to something it can't turn; standing, turning lets go and it reaches again. What's carried turns with it.
        if (move !== 0 && move !== (this.flipped ? -1 : 1) && !this.ledge && !this.down && !this.rise && !this.roll && !roped && !(this.holding() && this.anchored && !this.grounded)) {
            if (this.holding() && !this.anchored) this.turnCarrying();
            else {
                this.letGoHold();
                this.character.flip();
            }
        }
        const s = this.flipped ? -1 : 1;

        let mass = 0;
        let comX = 0;
        let comY = 0;
        let velocityX = 0;
        let velocityY = 0;
        parts.forEach(body => {
            const m = body.GetMass();
            const center = body.GetWorldCenter();
            const velocity = body.GetLinearVelocity();
            mass += m;
            comX += m * center.x;
            comY += m * center.y;
            velocityX += m * velocity.x;
            velocityY += m * velocity.y;
        });
        comX /= mass;
        comY /= mass;
        velocityX /= mass;
        velocityY /= mass;
        // How fast the body turns as a whole: its angular momentum about the centre of mass over its inertia.
        let momentum = 0;
        let inertia = 0;
        parts.forEach(body => {
            const m = body.GetMass();
            const center = body.GetWorldCenter();
            const velocity = body.GetLinearVelocity();
            const rx = center.x - comX;
            const ry = center.y - comY;
            const own = body.GetInertia() - m * body.GetLocalCenter().LengthSquared();
            momentum += own * body.GetAngularVelocity() + m * (rx * (velocity.y - velocityY) - ry * (velocity.x - velocityX));
            inertia += own + m * (rx * rx + ry * ry);
        });
        const spinSpeed = momentum / inertia;

        // Hit hard: the body's speed changed by more than gravity and the runner's own pushes explain. Going through a
        // portal turns the velocity, which isn't a hit, and neither is a rope pulling taut or a launch.
        const track = this.track;
        const teleported = this.character.teleportTicks > 0 || this.character.isPortalTransitActive?.();
        if (track && !this.down && !teleported && !roped && !this.launched && !this.holding()) {
            const hit = Math.hypot(velocityX - track.velocityX - track.pushedX - track.gx * track.dt, velocityY - track.velocityY - track.pushedY - track.gy * track.dt);
            if (hit > RAGDOLL.impact) this.knockDown();
            this.stagger = Math.max(clamp((hit - REACT.stagger) / (RAGDOLL.impact - REACT.stagger), 0, 1), this.stagger - ms / REACT.recover);
        }
        this.track = {velocityX, velocityY, pushedX: 0, pushedY: 0, gx, gy, dt};
        this.react(gx, gy, dt, ms);
        // Launched, the runner leaves whatever it stood on or held and flies with its air jump still to come.
        if (this.launched && !this.down && !this.rise) {
            if (this.ledge) this.letGo(1);
            this.wall = null;
            this.roll = null;
            this.jumpIgnore = JUMP.groundIgnore;
            this.jumpsUsed = Math.max(this.jumpsUsed, 1);
            this.jumpRise = null;
            this.airTime = Math.max(this.airTime, JUMP.coyote + 1);
        }
        this.launched = false;

        this.jumpIgnore = Math.max(0, this.jumpIgnore - ms);
        this.jumpBuffer = Math.max(0, this.jumpBuffer - ms);
        this.grabCooldown = Math.max(0, this.grabCooldown - ms);
        if (this.lastWall && (this.lastWall.time -= ms) <= 0) this.lastWall = null;
        const pressedDown = this.vertical < 0 && this.lastVertical >= 0;
        this.rollBuffer = pressedDown ? ROLL.buffer : Math.max(0, this.rollBuffer - ms);
        const arms = SIDES.filter(side => this.hasArm(side));
        const context = {parts, torso, up, right, upAngle, dt, ms, gx, gy, mass, legs, arms, move, comX, comY, velocityX, velocityY, spinSpeed};
        this.bodyMass = mass;
        this.upAngle = upAngle;
        this.armTouching = {left: this.touching(CAUGHT[0]), right: this.touching(CAUGHT[1])};
        if (this.down) {
            this.updateDown(context);
            this.lastVertical = this.vertical;
            return;
        }
        if (this.rise) {
            this.updateRise(context);
            this.lastVertical = this.vertical;
            return;
        }
        if (roped) {
            this.updateCarried(context);
            this.lastVertical = this.vertical;
            return;
        }
        this.regrab = Math.max(0, (this.regrab ?? 0) - ms);
        if (this.holding()) {
            if (!this.grabHeld || this.ledge || this.roll || arms.length === 0) this.letGoHold();
            else if (this.anchored && !this.feetDown(legs, up)) {
                this.updateSwing(context);
                this.lastVertical = this.vertical;
                return;
            }
        }
        if (this.ledge) {
            this.updateLedge(context);
            this.lastVertical = this.vertical;
            return;
        }
        if (this.roll && this.updateRoll(context)) {
            this.lastVertical = this.vertical;
            return;
        }

        const hips = legs.map(side => this.hipPoint(this.legs[side]));
        const hip = {x: hips.reduce((sum, point) => sum + point.x, 0) / hips.length, y: hips.reduce((sum, point) => sum + point.y, 0) / hips.length};

        let crouching = this.vertical < 0 || Boolean(this.stoop);
        const standHeight = this.reach * POSE.stand + this.ankleHeight;
        const runHeight = this.reach * POSE.run + this.ankleHeight;
        const crouchHeight = this.reach * POSE.crouch + this.ankleHeight;

        // Ground under the hips and a little ahead, so steps are climbed before the feet reach them. The step ahead only
        // counts with ground under the hips too: a body hanging off an edge isn't standing on it.
        const probeLength = standHeight + HOVER.snap + HOVER.step;
        // Standing, something loose more than a step above the ground it stood on isn't stood on: it's been run into, a crate
        // tipped in under the hips, and the ground is looked for under it.
        const lastHeight = this.grounded ? this.groundHeight : undefined;
        const groundProbe = (x, y) => {
            let hit = this.probe(x, y, up, probeLength);
            while (hit && lastHeight !== undefined && hit.body.GetType() === Box2D.b2_dynamicBody && hit.x * up.x + hit.y * up.y > lastHeight + HOVER.step) {
                const through = this.probe(hit.x - up.x * HOVER.through, hit.y - up.y * HOVER.through, up, probeLength - hit.distance - HOVER.through);
                hit = through && {...through, distance: through.distance + hit.distance + HOVER.through};
            }
            return hit;
        };
        let ground = null;
        const found = [];
        [0, HOVER.probe].forEach(ahead => {
            if (ahead > 0 && !ground) return;
            const hit = groundProbe(hip.x + right.x * s * ahead, hip.y + right.y * s * ahead);
            if (!hit || hit.nx * up.x + hit.ny * up.y < HOVER.slope) return;
            // Only a step up: anything taller ahead, a crate walked up against, is pushed or climbed, not stood on.
            if (ground && ground.distance - hit.distance > HOVER.step) return;
            found.push(hit);
            // The same flat slope going on up holds it up with the rest, but isn't what the hips ride above: that high, the
            // feet couldn't reach the ground.
            if (ground && hit.body === ground.body && hit.nx * ground.nx + hit.ny * ground.ny > HOVER.sameSlope) {
                const normal = {x: ground.nx, y: ground.ny};
                const rise = ahead * (right.x * s * normal.x + right.y * s * normal.y) / (up.x * normal.x + up.y * normal.y);
                if (hit.distance > ground.distance + rise - HOVER.stick) return;
            }
            if (!ground || hit.distance < ground.distance) ground = hit;
        });
        // Hips just past an edge stand on it, unless running off it, and over a gap narrow enough to straddle the ground
        // either side holds them up, whether or not the gap's bottom is in sight.
        if (!ground || ground.distance > standHeight + HOVER.snap) {
            const side = (distance, direction) => {
                const hit = groundProbe(hip.x + right.x * direction * distance, hip.y + right.y * direction * distance);
                return hit && hit.nx * up.x + hit.ny * up.y >= HOVER.slope && hit.distance <= standHeight + HOVER.snap ? hit : null;
            };
            const slow = Math.abs(velocityX * right.x + velocityY * right.y) < MOVE.walk;
            const near = slow ? [side(HOVER.edge, -1), side(HOVER.edge, 1)].filter(Boolean) : [];
            const far = [side(HOVER.straddle, -1), side(HOVER.straddle, 1)];
            const holds = far[0] && far[1] ? [...near, ...far] : near;
            holds.forEach(hit => {
                found.push(hit);
                if (!ground || hit.distance < ground.distance) ground = hit;
            });
        }
        // Standing across two things about as high, it keeps to the one it's on, rather than switching between them and
        // following one then the other.
        if (ground && ground.body !== this.groundBody) {
            const kept = found.filter(hit => hit.body === this.groundBody && hit.distance <= ground.distance + HOVER.stick)
                .sort((a, b) => a.distance - b.distance)[0];
            if (kept) ground = kept;
        }
        // Everything about as high as it under the runner shares its weight and its push, and carries it along together:
        // all of it on one barrel of two, off its top, would roll it away.
        const supports = ground ? [ground, ...found.filter(hit => hit.body !== ground.body && hit.distance <= ground.distance + HOVER.stick)
            .filter((hit, i, all) => all.findIndex(other => other.body === hit.body) === i)] : [];
        const wasGrounded = this.grounded;
        // After a jump the ground only catches the body again once it stops rising.
        const rising = this.jumpsUsed > 0 && !wasGrounded && (velocityX * up.x + velocityY * up.y) > JUMP.reland;
        // Standing needs the body roughly upright: holding the hips up over a head that's under them crushes the neck.
        const upright = Math.abs(wrapAngle(torso.GetAngle() - upAngle)) < UPRIGHT.stand;
        this.grounded = Boolean(ground) && upright && this.jumpIgnore === 0 && !rising && !flying && ground.distance <= standHeight + HOVER.snap;
        if (!this.grounded) ground = null;
        this.groundBody = ground?.body ?? null;
        this.supportBodies = new Set(supports.map(hit => hit.body));
        this.groundHeight = ground ? ground.x * up.x + ground.y * up.y : undefined;
        // With no room to stand up the runner stays crouched, and ducks ahead of a low gap it's running into.
        if (!ground) this.ducked = 0;
        else if (!crouching) {
            const speed = (velocityX * right.x + velocityY * right.y) * s;
            const height = mix(standHeight, runHeight, clamp((Math.abs(speed) - MOVE.walk) / (MOVE.run - MOVE.walk), 0, 1));
            const low = this.lowCeiling(hip, up, {x: right.x * s, y: right.y * s}, ground.distance, speed, this.ducked > 0 ? standHeight : height,
                this.ducked > 0 ? CEILING.stand : CEILING.duck);
            this.ducked = low ? CEILING.hold : Math.max(0, this.ducked - ms);
            crouching = this.ducked > 0;
        }

        const carried = {x: 0, y: 0};
        supports.forEach(hit => {
            const velocity = this.carriedVelocity(hit);
            carried.x += velocity.x / supports.length;
            carried.y += velocity.y / supports.length;
        });
        // Standing on it, what carries the runner can't throw it: the corner of a tumbling crate or the rim of a spinning
        // wheel changes speed under it faster than anything to stand on, so the speed it's carried at follows no faster
        // than the hover can.
        if (wasGrounded && this.carried) {
            const changeX = carried.x - this.carried.x;
            const changeY = carried.y - this.carried.y;
            const scale = Math.min(1, HOVER.maxAccel * dt / (Math.hypot(changeX, changeY) || 1));
            carried.x = this.carried.x + changeX * scale;
            carried.y = this.carried.y + changeY * scale;
        }
        this.carried = ground ? carried : null;
        const relativeX = velocityX - carried.x;
        const relativeY = velocityY - carried.y;
        const upSpeed = relativeX * up.x + relativeY * up.y;

        const comHeight = comX * up.x + comY * up.y;
        if (this.grounded) {
            this.wall = null;
            this.lastWall = null;
            this.kicked = null;
            if (!wasGrounded && this.airTime > JUMP.coyote) {
                this.landTime = 0;
                this.landDepth = POSE.land * clamp(this.fallSpeed / 20, 0.2, 1);
                // Landing from a long fall is a crash too, unless it's rolled and the drop wasn't too far.
                const drop = this.apex === null ? 0 : this.apex - comHeight;
                this.sound(SOUND.land[this.sounds % 2], clamp(this.fallSpeed / 40, 0.08, 0.5), torso.GetPosition());
                const rolling = (this.vertical < 0 || this.rollBuffer > 0) && this.fallSpeed > ROLL.minFall && legs.length === 2;
                if (rolling ? drop > ROLL.maxDrop : this.fallSpeed > RAGDOLL.fall) {
                    this.knockDown();
                    return;
                }
                if (rolling) {
                    this.startRoll(context, hip);
                    this.updateRoll(context);
                    this.lastVertical = this.vertical;
                    return;
                }
            }
            this.apex = null;
            this.airTime = 0;
            this.jumpsUsed = 0;
            this.flipTime = -1;
            this.jumpRise = null;
            // Pressing down while running dives into a roll.
            if (pressedDown && legs.length === 2 && this.gaitSpeed > ROLL.dive && !this.findWall(right.x * s, right.y * s, ROLL.wall * 2)) {
                this.startRoll(context, hip);
                this.updateRoll(context);
                this.lastVertical = this.vertical;
                return;
            }
        } else {
            this.airTime += ms;
            if (this.airTime > JUMP.coyote) this.jumpsUsed = Math.max(this.jumpsUsed, 1);
            this.fallSpeed = Math.max(0, -upSpeed);
            if (upSpeed <= 0) this.jumpRise = null;
            this.apex = Math.max(this.apex ?? comHeight, comHeight);
        }
        // Holding down the roll starts in the air, just before the ground, unless the drop is too far to roll out of.
        if (!this.grounded && !this.roll && (this.vertical < 0 || this.rollBuffer > 0) && legs.length === 2 && this.fallSpeed > ROLL.minFall && this.flipTime < 0) {
            const below = this.probe(comX, comY, up, ROLL.lookAhead);
            if (below && below.nx * up.x + below.ny * up.y > HOVER.slope) {
                const gap = below.distance - this.depthBelow(parts, up, comX, comY);
                const landing = comHeight - below.distance + standHeight + (torso.GetPosition().x - hip.x) * up.x + (torso.GetPosition().y - hip.y) * up.y;
                if (gap / this.fallSpeed / dt * ms < ROLL.lead && (this.apex ?? comHeight) - landing <= ROLL.maxDrop) {
                    this.startRoll(context, hip, true);
                    this.updateRoll(context);
                    this.lastVertical = this.vertical;
                    return;
                }
            }
        }

        // Catch ledges on the way up near the top of a jump or on the way down; an arm already hooked over one catches it too.
        // Where the hips would hang within standing height of the floor there's no room to hang: only climbing catches it.
        const hooked = arms.length > 0 && this.armStrain(arms) > LEDGE.strain;
        if (!this.grounded && arms.length > 0 && !this.grabHeld) {
            // Arms down at the sides can't swing up past a wall to catch anything.
            const armUp = arms.some(side => s * this.joint(`shoulder_${side}_joint`).GetJointAngle() > LEDGE.reachArm * DEG);
            const reaching = armUp && this.vertical >= 0 && this.airTime > LEDGE.after && upSpeed < LEDGE.maxRise && this.flipTime < 0;
            let ledge = (reaching || hooked) && this.grabCooldown === 0 && this.findLedge(arms, up, {x: right.x * s, y: right.y * s});
            // Above a top the runner only vaults on when flying at it; going up or down past it, the edge is caught once
            // the shoulders are below it.
            const towards = relativeX * right.x * s + relativeY * right.y * s;
            if (ledge && ledge.height < 0 && (towards < LEDGE.vaultSpeed || upSpeed < -LEDGE.vaultFall)) ledge = null;
            if (ledge && this.vertical <= 0 && ledge.height >= 0) {
                const hipBelow = LEDGE.depth + (torso.GetPosition().x - hip.x) * up.x + (torso.GetPosition().y - hip.y) * up.y;
                const hangX = ledge.corner.x - right.x * s * LEDGE.back - up.x * hipBelow;
                const hangY = ledge.corner.y - right.y * s * LEDGE.back - up.y * hipBelow;
                if (this.probe(hangX, hangY, up, standHeight + HOVER.snap + HOVER.step)) ledge = null;
                // A hang is caught at arm's length, the hands at the lip; higher up the runner keeps reaching for it.
                const depth = (ledge?.corner.x - torso.GetPosition().x) * up.x + (ledge?.corner.y - torso.GetPosition().y) * up.y;
                if (depth < LEDGE.depth - LEDGE.catchBand) ledge = null;
            }
            if (ledge) {
                this.grabLedge(ledge, torso, up, {x: right.x * s, y: right.y * s});
                this.updateLedge(context);
                this.lastVertical = this.vertical;
                return;
            }
        }
        if (this.grounded || !this.updateWall(context)) this.wall = null;
        else {
            this.lastVertical = this.vertical;
            return;
        }

        // In the air an arm pressed against or hooked over something goes limp and slides along it instead of pushing the
        // body away.
        if (hooked && !this.grounded) this.limpArms = arms;
        // Running builds up while a direction is held, or carries on at the speed already going.
        this.holdTime = move !== 0 ? this.holdTime + ms : 0;
        const runShare = clamp((this.holdTime - MOVE.runAfter) / MOVE.runRamp, 0, 1);
        // A stump limps and one leg hops, both slower.
        const hopping = legs.length === 1;
        const crippled = legs.some(side => this.legs[side].stump);
        const topSpeed = hopping ? HOP.speed : crippled ? CRIPPLE.speed : MOVE.run;
        let targetSpeed = move !== 0 ? Math.min(mix(MOVE.walk, MOVE.run, runShare), topSpeed) : 0;
        if (crouching && this.grounded) targetSpeed = Math.min(targetSpeed, MOVE.crouch);
        // Running into a wall stops instead of treading against it, far enough off it for the arms to come up.
        const forwardX = right.x * s;
        const forwardY = right.y * s;
        const chest = torso.GetPosition();
        // Loose objects aren't walls: the runner pushes them.
        const wallDistance = (point, length) => {
            const hit = this.cast(point.x, point.y, forwardX, forwardY, length);
            return hit && hit.body.GetType() !== Box2D.b2_dynamicBody && hit.nx * forwardX + hit.ny * forwardY < -MOVE.wallFacing ? hit.distance : Infinity;
        };
        const chestToWall = wallDistance(chest, WALL.reach);
        // Something at head height ahead is braked for like a wall, the head kept headClear m off it.
        const headPosition = rider[Humanoid.BODY_PARTS.HEAD]?.GetPosition();
        const headPoint = headPosition && {x: headPosition.x, y: headPosition.y};
        const wallAheadOf = length => Math.min(wallDistance(chest, length), headPoint ? wallDistance(headPoint, length) + WALL.standOff - JAM.headClear : Infinity);
        // Running at a wall the arms come up on the way, so they don't have to swing up past it once there.
        this.wallApproach = move !== 0 ? clamp((WALL.raise - wallDistance(chest, WALL.raise)) / (WALL.raise - WALL.standOff - WALL.settle), 0, 1) : 0;
        const flipRoom = wallDistance(chest, WALL.flip) === Infinity;
        // In the air the runner closes in on the wall to reach a ledge above, as close as it hangs: closer, the knees jam into it.
        const atWall = move !== 0 && (chestToWall < (this.grounded ? WALL.standOff + WALL.settle : WALL.air) || wallDistance(hip, MOVE.wallReach) < Infinity);
        // The head or body pressed against something solid ahead stops the runner pushing on; with the neck already pulled
        // it eases back, rather than walk on out from under its own head.
        const neck = this.strain([Humanoid.BODY_PARTS.HEAD]);
        // Holding on to something fixed it goes no further from it than its arms reach.
        const jammed = move !== 0 && (neck > JAM.neck || this.pressedAhead({x: right.x * s, y: right.y * s}) || this.atArmsLength(arms, {x: right.x * s, y: right.y * s}));
        const blocked = atWall || jammed;
        if (blocked) {
            targetSpeed = this.grounded ? Math.min(0, (chestToWall - WALL.standOff) * WALL.backOff) : 0;
            if (neck > JAM.neck) targetSpeed = -JAM.backOff;
            this.holdTime = 0;
        }
        // Wedged against a wall in front, with joints pulled apart for a while, the runner pushes off it and lets its arms
        // go before anything tears. With the chest clear the body is going over, and caught limbs slip free instead.
        this.wedged = this.strain(LIMBS) > LEDGE.tear ? this.wedged + 1 : 0;
        if (this.wedged > WEDGE.frames && chestToWall < WALL.reach) {
            this.push(parts, -forwardX * WEDGE.push, -forwardY * WEDGE.push);
            this.limpArms = arms;
            this.flipTime = -1;
            this.grabCooldown = LEDGE.cooldown;
            this.wedged = 0;
        }

        let applyX = 0;
        let applyY = 0;
        let forwardSpeed;
        let tangent;
        if (ground) {
            const length = Math.hypot(ground.nx, ground.ny);
            const normal = {x: ground.nx / length, y: ground.ny / length};
            tangent = {x: -normal.y * s, y: normal.x * s};
            forwardSpeed = relativeX * tangent.x + relativeY * tangent.y;
            if (move !== 0 && !crouching && !blocked) targetSpeed = Math.max(targetSpeed, Math.min(forwardSpeed, topSpeed));
            // A wall ahead is braked for in time to stop at the stand-off, with room to bring the arms up.
            const wallAhead = wallAheadOf(Math.max(WALL.reach, forwardSpeed * forwardSpeed / (2 * MOVE.brake * WALL.arrive) + WALL.standOff + WALL.settle));
            if (wallAhead < Infinity) targetSpeed = Math.min(targetSpeed, Math.sqrt(2 * MOVE.brake * WALL.arrive * Math.max(0, wallAhead - WALL.standOff)));
            // Run into, the legs would hit it and the body be thrown up over it.
            const push = this.pushAhead(hip, up, tangent, ground.distance);
            if (push) {
                const pushSpeed = PUSH.speed * clamp(mass / push.body.GetMass(), 1, MOVE.run / PUSH.speed);
                vec1.Set(push.x, push.y);
                const pushed = push.body.GetLinearVelocityFromWorldPoint(vec1);
                const along = (pushed.x - carried.x) * tangent.x + (pushed.y - carried.y) * tangent.y;
                const gap = push.distance - PUSH.contact;
                targetSpeed = Math.min(targetSpeed, along + (gap > 0 ? Math.sqrt(2 * MOVE.brake * gap) : gap * WALL.backOff));
                // Shoved through its middle it slides, rather than tipping over and tumbling under the runner.
                if (move !== 0 && gap < PUSH.touch && along < pushSpeed) {
                    vec1.Set(tangent.x * mass * PUSH.force, tangent.y * mass * PUSH.force);
                    push.body.ApplyForceToCenter(vec1, true);
                }
            }
            // Stopping from a run skids to a halt and ducking slides on down to a crouch; walls are braked for at full strength.
            let accel = forwardSpeed < 0 ? MOVE.turnBrake : targetSpeed > forwardSpeed ? MOVE.accel : wallAhead < Infinity ? MOVE.brake
                : crouching ? MOVE.crouchBrake : forwardSpeed > MOVE.walk ? MOVE.skidBrake : MOVE.brake;
            // Ducking faster than a duck walk slides along on planted feet.
            this.braking = wallAhead === Infinity && forwardSpeed > SKID.minSpeed && (crouching ? forwardSpeed > MOVE.crouch + SKID.minSpeed : targetSpeed < forwardSpeed - SKID.minSpeed);
            if (hopping && targetSpeed > forwardSpeed) accel = HOP.accel;
            // Faster than it can run, from a launch or a slope, the runner slides down to speed instead of stopping dead.
            if (forwardSpeed > topSpeed) accel = MOVE.overBrake;
            const change = clamp(targetSpeed - forwardSpeed, -accel * dt, accel * dt);
            applyX += tangent.x * change;
            applyY += tangent.y * change;

            // Hover: hold the hips at the pose's height above the ground and cancel gravity.
            const runBlend = clamp((Math.abs(forwardSpeed) - MOVE.walk) / (MOVE.run - MOVE.walk), 0, 1);
            let height = crouching ? crouchHeight : mix(standHeight, runHeight, runBlend);
            // On a slope the downhill foot stands lower than the ground under the hips: they sink for it to reach.
            height -= (GAIT.stance + FOOTING.half) * Math.abs(normal.x * right.x + normal.y * right.y) / Math.max(HOVER.slope, normal.x * up.x + normal.y * up.y);
            if (this.landTime >= 0) {
                height -= this.landDepth * this.reach * Math.sin(Math.PI * Math.min(1, this.landTime / POSE.landTime));
                this.landTime += ms;
                if (this.landTime > POSE.landTime) this.landTime = -1;
            }
            // The stride's bob, followed at its own speed: the spring alone lags a running bob by half a step.
            height += crouching ? 0 : this.bob * this.reach;
            const heightSpeed = this.rideHeight === undefined || !wasGrounded ? 0 : clamp((height - this.rideHeight) / dt, -2, 2);
            const slopeSpeed = forwardSpeed * (tangent.x * up.x + tangent.y * up.y);
            // Lifted onto something higher, the body rises no faster than maxRise off it: at full spring it would overshoot
            // and be thrown into the air.
            const lift = Math.min(clamp(HOVER.stiffness * (height - ground.distance) - HOVER.damping * (upSpeed - slopeSpeed - heightSpeed), -HOVER.maxAccel, HOVER.maxAccel),
                Math.max(0, (HOVER.maxRise - (upSpeed - slopeSpeed)) / dt));
            applyX += (up.x * lift - gx) * dt;
            applyY += (up.y * lift - gy) * dt;
            this.rideHeight = height;

            // Whatever carries the runner takes its weight, and of its push and the hover's spring only its share by mass: in
            // full, a light thing kicked by them would come straight back into them and the two would shake.
            supports.forEach(hit => {
                if (hit.body.GetType() !== Box2D.b2_dynamicBody) return;
                const share = mass / supports.length;
                const push = change / dt * this.pushShare(hit, tangent, share);
                const spring = lift * this.pushShare(hit, up, share);
                vec1.Set(share * (gx - tangent.x * push - up.x * spring), share * (gy - tangent.y * push - up.y * spring));
                vec2.Set(hit.x, hit.y);
                hit.body.ApplyForce(vec1, vec2, true);
            });
        } else {
            tangent = {x: right.x * s, y: right.y * s};
            forwardSpeed = relativeX * tangent.x + relativeY * tangent.y;
            let change = 0;
            // Flying at a wall the runner slows to arrive at it, not slam into it and catch limbs over its top.
            const wallAhead = wallAheadOf(Math.max(WALL.reach, forwardSpeed * WALL.lookAhead));
            const arrival = Math.sqrt(2 * WALL.brake * Math.max(0, wallAhead - WALL.air));
            if (forwardSpeed > arrival) {
                change = -Math.min(forwardSpeed - arrival, WALL.brake * dt);
            } else if (move !== 0) {
                const airTarget = blocked ? 0 : Math.max(targetSpeed, MOVE.airSpeed);
                if (forwardSpeed < airTarget) change = Math.min(airTarget - forwardSpeed, MOVE.airAccel * dt);
                else if (forwardSpeed > MOVE.run) change = -Math.min(forwardSpeed - MOVE.run, MOVE.airDrag * dt);
            } else {
                change = -clamp(forwardSpeed, -MOVE.airDrag * dt, MOVE.airDrag * dt);
            }
            applyX += tangent.x * change;
            applyY += tangent.y * change;

            let weight = 1;
            if (flying) weight = 1;
            else if (upSpeed <= 0) weight = JUMP.fallGravity;
            else if (this.jumpRise === 'held') weight = Math.abs(upSpeed) < JUMP.apexSpeed ? JUMP.apexGravity : JUMP.riseGravity;
            else if (this.jumpRise === 'cut') weight = JUMP.cutGravity;
            applyX += gx * (weight - 1) * dt;
            applyY += gy * (weight - 1) * dt;
            const fall = -(upSpeed + applyX * up.x + applyY * up.y);
            if (fall > JUMP.maxFall) {
                applyX += up.x * (fall - JUMP.maxFall);
                applyY += up.y * (fall - JUMP.maxFall);
            }
        }

        // Jumps: from the ground (or just after leaving it), then one more in the air.
        let upChange = 0;
        // One leg moves in hops while it's steered or still sliding.
        this.hopTime += ms;
        if (hopping && this.grounded && this.jumpBuffer === 0 && (move !== 0 || Math.abs(forwardSpeed) > 1) && this.hopTime > HOP.period) {
            upChange = HOP.up - upSpeed;
            this.hopTime = 0;
            this.jumpIgnore = HOP.ignore;
            this.grounded = false;
        }
        if (this.jumpBuffer > 0) {
            const fromGround = this.jumpsUsed === 0 && (this.grounded || this.airTime <= JUMP.coyote);
            // A press just before landing waits for the ground instead of spending the air jump.
            const landing = !fromGround && upSpeed < 0 && this.probe(hip.x, hip.y, up, standHeight + HOVER.snap - upSpeed * this.jumpBuffer * dt / ms);
            // Beside a wall the air jump kicks off it instead.
            const wall = !fromGround && !landing && this.kickWall(context, move);
            if (wall) {
                this.kick(context, wall);
                this.lastVertical = this.vertical;
                return;
            }
            if (fromGround || (this.jumpsUsed < 2 && !landing)) {
                const speed = (fromGround ? JUMP.speed : JUMP.doubleSpeed) * (hopping ? HOP.jump : crippled ? CRIPPLE.jump : 1);
                upChange = speed - (upSpeed + applyX * up.x + applyY * up.y);
                if (!fromGround) upChange = Math.max(upChange, 0);
                this.jumpsUsed = fromGround ? 1 : 2;
                this.sound(fromGround ? SOUND.jump : SOUND.flip, fromGround ? 0.15 : 0.25, torso.GetPosition());
                this.jumpBuffer = 0;
                this.jumpIgnore = JUMP.groundIgnore;
                this.jumpRise = this.jumpHeld ? 'held' : 'cut';
                this.grounded = false;
                this.landTime = -1;
                if (!fromGround) {
                    // No room to roll through a flip with a wall right ahead: the air jump goes straight up.
                    if (flipRoom) {
                        this.flipTime = 0;
                        this.flipAngle = torso.GetAngle();
                    }
                    // An air jump also turns the runner's momentum the way it's steering.
                    if (move !== 0 && forwardSpeed + applyX * tangent.x + applyY * tangent.y < MOVE.airSpeed) {
                        const turn = MOVE.airSpeed - (forwardSpeed + applyX * tangent.x + applyY * tangent.y);
                        applyX += tangent.x * turn;
                        applyY += tangent.y * turn;
                    }
                }
            }
        }
        if (this.releaseJump && this.jumpRise === 'held') this.jumpRise = 'cut';
        this.releaseJump = false;
        applyX += up.x * upChange;
        applyY += up.y * upChange;
        this.push(parts, applyX, applyY);

        // Upright, leaning into speed; the double jump rolls through a front flip.
        let lean = POSE.lean;
        if (this.grounded) lean += (POSE.runLean - POSE.lean) * clamp(this.gaitSpeed / MOVE.run, -1, 1) + this.bodyLean;
        if (crouching && this.grounded) lean += POSE.crouchLean;
        let error;
        let spinTarget;
        // A flip that nears a wall stops and rights itself rather than fold the body up against it.
        if (this.flipTime >= 0 && !flipRoom) this.flipTime = -1;
        if (this.flipTime >= 0) {
            this.flipTime += ms;
            const t = Math.min(1, this.flipTime / JUMP.flipTime);
            error = this.flipAngle + s * Math.PI * 2 * smooth(t) - torso.GetAngle();
            // Follow the flip's own turning speed as well as its angle.
            spinTarget = error * UPRIGHT.flipRate + s * Math.PI * 2 * 6 * t * (1 - t) / (JUMP.flipTime / 1000 * Settings.physicsTimeStep * 1000 / Settings.timeStep);
            if (t >= 1) this.flipTime = -1;
        } else {
            error = wrapAngle(upAngle + s * lean - torso.GetAngle());
            spinTarget = error * (this.grounded ? UPRIGHT.rate : UPRIGHT.airRate);
        }
        spinTarget = clamp(spinTarget, -UPRIGHT.maxSpin * 2, UPRIGHT.maxSpin * 2);
        if (this.flipTime < 0) spinTarget = clamp(spinTarget, -UPRIGHT.maxSpin, UPRIGHT.maxSpin);
        this.spin(parts, (spinTarget - spinSpeed) * UPRIGHT.response * (1 - REACT.balance * this.stagger), comX, comY);
        this.pelvisAngle = torso.GetAngle() + error + s * this.waistRest;

        if (this.grounded) this.animateGround(legs, hip, up, tangent, forwardSpeed, dt, ms, crouching, move !== 0 && !blocked);
        else this.animateAir(legs, torso, upSpeed, forwardSpeed, chestToWall < WALL.reach, ms, hip, up, standHeight, dt);
        if (this.grounded && atWall) this.driveArms(AIR.wall.arms, -HEAD.wall);
        this.updateHands(context);
        // A hooked arm without a ledge to hang from goes limp and slides off.
        this.limpArms?.forEach(side => ['shoulder', 'arm', 'hand'].forEach(part => delete this.pose[`${part}_${side}_joint`]));
        this.limpArms = null;
        this.freeCaughtParts();
        this.lastVertical = this.vertical;
    }
    // A limb caught on the far side of something, pulled apart for a while, slips through it instead of tearing off,
    // and collides again once back with the body. Triggers and the physics camera still see it.
    freeCaughtParts() {
        const rider = this.character.lookupObject;
        CAUGHT.forEach(limb => {
            const parts = limb.map(part => rider[part]).filter(body => body && !body.snapped && !body.destroyed);
            if (parts.length === 0) return;
            const strain = this.strain(limb);
            const tracker = parts[0];
            tracker.caughtFrames = strain > LEDGE.tear ? (tracker.caughtFrames ?? 0) + 1 : 0;
            if (!tracker.slipping && tracker.caughtFrames > WEDGE.slip) parts.forEach(body => this.setSlipping(body, true));
            else if (tracker.slipping && strain < WEDGE.rejoin) parts.forEach(body => this.setSlipping(body, false));
        });
    }
    setSlipping(body, slipping) {
        body.slipping = slipping;
        const keep = game.editor.MASKBIT_TRIGGER | game.editor.MASKBIT_PHYSICS_CULL;
        for (let fixture = body.GetFixtureList(); Box2D.getPointer(fixture) !== Box2D.getPointer(Box2D.NULL); fixture = fixture.GetNext()) {
            const filter = fixture.GetFilterData();
            if (slipping) fixture.slipMask = filter.get_maskBits();
            filter.set_maskBits(slipping ? fixture.slipMask & keep : fixture.slipMask);
            fixture.SetFilterData(filter);
        }
    }
    animateGround(legs, hip, up, tangent, forwardSpeed, dt, ms, crouching, steering) {
        const s = this.flipped ? -1 : 1;
        // Steps follow a smoothed speed, so a wobbling body doesn't start them.
        const lastSpeed = this.gaitSpeed;
        this.gaitSpeed += (forwardSpeed - this.gaitSpeed) * GAIT.smoothing;
        const speed = this.gaitSpeed;
        this.accel += ((speed - lastSpeed) / dt - this.accel) * (1 - Math.exp(-ms / BODY.accelTime));
        const walking = steering || Math.abs(speed) > GAIT.idleSpeed;
        this.skid = this.braking ? Math.min(1, (this.skid ?? 0) + ms / SKID.attack) : Math.max(0, (this.skid ?? 0) - ms / SKID.release);
        const skid = smooth(this.skid);
        this.moving = clamp(this.moving + (walking ? 1 : -1) * ms / GAIT.settle, 0, 1);
        // Crouched, even fast, the legs keep a walking stride: a running one kicks higher than the low hips allow.
        const runShare = crouching ? 0 : smooth(clamp((Math.abs(speed) - MOVE.walk) / (MOVE.run - MOVE.walk), 0, 1));
        const cycle = mix(GAIT.walkCycle, GAIT.runCycle, runShare);
        const duty = mix(GAIT.walkDuty, GAIT.runDuty, runShare);
        const stride = duty * cycle;
        // Limping, the stump stands briefly just before the good foot comes down again, and the good leg stands longer.
        const limping = legs.length === 2 && legs.some(side => this.legs[side].stump);
        const dutyOf = side => !limping ? duty : this.legs[side].stump ? duty * CRIPPLE.stump : Math.min(CRIPPLE.maxDuty, duty * CRIPPLE.good);
        const offsetOf = side => !limping ? (side === 'left' ? 0 : 0.5) : this.legs[side].stump ? 1 - dutyOf(side) : 0;
        let limp = 0;
        const moving = smooth(this.moving);
        if (moving > 0) this.phase = ((this.phase + speed * dt / cycle) % 1 + 1) % 1;
        this.breath = (this.breath + ms / BODY.breathe) % 1;
        const breathing = Math.sin(this.breath * Math.PI * 2) * (1 - moving);

        // Twice a stride the body rises and falls, lowest on the standing leg running and highest over it walking.
        const step = ((this.phase - duty / 2) * 2 % 1 + 1) % 1;
        const bob = Math.cos(step * Math.PI * 2) * mix(BODY.walkBob, -BODY.runBob, runShare);
        this.bob = bob * moving * (legs.length === 2 ? 1 : 0) * (1 - skid) + breathing * BODY.breath - skid * SKID.sink;
        this.bodyLean = clamp(this.accel * BODY.accelLean, -BODY.maxAccelLean, BODY.maxAccelLean) * (crouching ? 0.5 : 1)
            + Math.sin(step * Math.PI * 2) * BODY.bobPitch * runShare * moving + breathing * BODY.breathLean;

        const forward = Math.atan2(tangent.y, tangent.x);
        const footPhases = {};
        // The skid plants the foot that is furthest forward or swinging forward.
        if (this.skid > 0 && !this.skidLead) {
            const ahead = side => {
                const phase = (this.phase + (side === 'left' ? 0 : 0.5)) % 1;
                return phase < duty ? 0.5 - phase / duty : (phase - duty) / (1 - duty) - 0.5;
            };
            this.skidLead = ahead('left') >= ahead('right') ? 'left' : 'right';
        }
        legs.forEach(side => {
            const leg = this.legs[side];
            const phase = (this.phase + offsetOf(side)) % 1;
            const legDuty = dutyOf(side);
            const legStride = legDuty * cycle;
            footPhases[side] = phase;
            if (leg.stump && limping && phase < legDuty) limp = Math.sin(Math.PI * phase / legDuty);
            // A foot coming down steps.
            if (phase < legDuty && (this.stepPhase?.[side] ?? 0) >= legDuty && moving > 0.5 && legs.length === 2) {
                this.sound(SOUND.step[side === 'left' ? 0 : 1], mix(...SOUND.stepVolume, runShare), hip);
            }
            (this.stepPhase ??= {})[side] = phase;
            let along;
            let lift = 0;
            let pitch = 0;
            if (phase < legDuty) {
                // Planted, the foot stays where it landed while the body passes over it, rolling up onto its toes to push off.
                along = legStride * (0.5 - phase / legDuty);
                const pushOff = smooth(clamp((phase / legDuty - 0.6) / 0.4, 0, 1));
                pitch = -pushOff * mix(0.25, 0.45, runShare);
            } else {
                const key = this.swingKey((phase - legDuty) / (1 - legDuty), runShare);
                along = legStride * key[0];
                lift = key[1] * this.reach;
                pitch = key[2];
            }
            const idle = side === 'left' ? GAIT.stance : -GAIT.stance;
            along = mix(idle, along, moving);
            lift *= moving;
            pitch *= moving;
            // Skidding, the foot that was further ahead plants out front and the other trails.
            if (skid > 0) {
                const lead = side === this.skidLead;
                along = mix(along, (lead ? SKID.lead : SKID.trail) * this.reach, skid);
                lift *= 1 - skid;
                pitch = mix(pitch, lead ? 0.25 : -0.2, skid);
            }
            // A stump has no toes to roll onto.
            if (leg.stump) pitch = 0;
            // A single leg stays under the body: it hops rather than steps.
            if (legs.length === 1) {
                along = 0;
                lift = 0;
                pitch = 0;
            }

            const probeX = hip.x + tangent.x * along;
            const probeY = hip.y + tangent.y * along;
            const footBody = this.character.lookupObject[`feet_${side}`];
            const current = footBody && !footBody.snapped && !footBody.destroyed ? footBody.GetPosition() : {x: probeX, y: probeY};
            const hit = this.footing(probeX, probeY, up, tangent, this.rideHeight + 3, {x: current.x, y: current.y});
            let point;
            let normal = up;
            if (hit && hit.nx * up.x + hit.ny * up.y > 0.3) {
                const length = Math.hypot(hit.nx, hit.ny);
                normal = {x: hit.nx / length, y: hit.ny / length};
                point = {x: hit.x, y: hit.y};
            } else {
                point = {x: probeX - up.x * this.rideHeight, y: probeY - up.y * this.rideHeight};
            }
            const footAngle = Math.atan2(normal.x, -normal.y) + s * pitch;
            const foot = this.withinReach(leg, {
                x: point.x + normal.x * this.footSole + up.x * lift,
                y: point.y + normal.y * this.footSole + up.y * lift,
            }, footAngle);
            this.driveLeg(leg, foot, footAngle, forward);
        });

        if (this.skid === 0) this.skidLead = null;
        // The body sinks and pitches forward onto the stump.
        this.bob -= CRIPPLE.dip * limp * moving;
        this.bodyLean += CRIPPLE.lurch * limp * moving;
        // Each arm swings forward with the other leg, bending more as it comes forward; the hands trail loosely.
        const walkArms = crouching ? ARMS.crouch : ARMS.walk;
        SIDES.forEach(side => {
            const other = footPhases[side === 'left' ? 'right' : 'left'] ?? (this.phase + (side === 'left' ? 0.5 : 0)) % 1;
            const swing = Math.cos((other - 0.04) * Math.PI * 2) * moving;
            const shoulder = mix(mix(ARMS.idle.shoulder, walkArms.shoulder, moving), ARMS.run.shoulder, runShare)
                + swing * mix(ARMS.walk.swing, ARMS.run.swing, runShare) + breathing * 2;
            const elbow = mix(mix(ARMS.idle.elbow, walkArms.elbow, moving), ARMS.run.elbow, runShare)
                + Math.max(0, swing) * mix(ARMS.walk.bend, ARMS.run.bend, runShare);
            const strength = this.armStrength(side, 1);
            const raise = smooth(this.wallApproach);
            this.drive(`shoulder_${side}_joint`, s * mix(mix(shoulder, SKID.arms[side][0], skid), AIR.wall.arms[side][0], raise) * DEG, strength);
            this.drive(`arm_${side}_joint`, s * mix(mix(elbow, SKID.arms[side][1], skid), AIR.wall.arms[side][1], raise) * DEG, strength);
            this.drive(`hand_${side}_joint`, 0, strength * ARMS.hand);
        });
        this.lookAt(crouching ? HEAD.crouch : mix(HEAD.stand, HEAD.run, runShare));
    }
    // The ground a foot goes down on near a spot. Off an edge, over a gap it can't reach the bottom of, the foot moves over
    // onto the ground with room for its heel and toe on whichever side of the gap it already is, the same spot however
    // far off the edge it was asked for.
    footing(x, y, up, tangent, length, foot) {
        // Ground just past the leg's reach, a curve falling away, is still stood on; only a step deeper is a gap.
        const reach = this.reach + this.ankleHeight + 1 + HOVER.step;
        const below = this.probe(x + up.x, y + up.y, up, length);
        const ground = new Map();
        const at = step => {
            if (!ground.has(step)) {
                const offset = step * FOOTING.step;
                const hit = this.probe(x + tangent.x * offset + up.x, y + tangent.y * offset + up.y, up, reach);
                ground.set(step, hit && hit.nx * up.x + hit.ny * up.y > 0.3 ? hit : null);
            }
            return ground.get(step);
        };
        const half = Math.round(FOOTING.half / FOOTING.step);
        const stands = step => at(step - half) && at(step + half) ? at(step) : null;
        if (below && below.distance <= reach && stands(0)) return below;
        const steps = Math.floor(FOOTING.reach / FOOTING.step);
        const nearest = direction => {
            for (let step = 1; step <= steps; step++) if (stands(direction * step)) return direction * step;
            return null;
        };
        const options = [nearest(1), nearest(-1)].filter(step => step !== null);
        if (options.length === 0) return below;
        const now = ((foot.x - x) * tangent.x + (foot.y - y) * tangent.y) / FOOTING.step;
        return at(options.reduce((best, step) => Math.abs(step - now) < Math.abs(best - now) ? step : best));
    }
    // A point along the swing's keys, smoothly through them.
    swingKey(t, runShare) {
        const keys = SWING.walk.map((walk, i) => walk.map((value, j) => mix(value, SWING.run[i][j], runShare)));
        const position = t * (keys.length - 1);
        const i = Math.min(keys.length - 2, Math.floor(position));
        const u = position - i;
        const p0 = keys[Math.max(0, i - 1)];
        const p1 = keys[i];
        const p2 = keys[i + 1];
        const p3 = keys[Math.min(keys.length - 1, i + 2)];
        return p1.map((_, j) => 0.5 * (2 * p1[j] + (p2[j] - p0[j]) * u + (2 * p0[j] - 5 * p1[j] + 4 * p2[j] - p3[j]) * u * u + (3 * p1[j] - p0[j] - 3 * p2[j] + p3[j]) * u * u * u));
    }
    // The head holds its own angle to gravity, nodded forward by pitch, whatever the torso does under it.
    lookAt(pitch) {
        const torso = this.character.lookupObject[Humanoid.BODY_PARTS.BODY];
        this.drive('head_joint', torso.GetAngle() - (this.upAngle + (this.flipped ? -1 : 1) * pitch));
    }
    // Along a wall the feet stay under the body: reaching forward they'd push into it.
    animateAir(legs, torso, upSpeed, forwardSpeed, wall, ms, hip, up, standHeight, dt) {
        const s = this.flipped ? -1 : 1;
        const running = clamp((forwardSpeed - MOVE.walk) / (MOVE.run - MOVE.walk), 0, 1);
        // Rising turns into falling around the top of the jump rather than at one instant.
        const falling = smooth(clamp(0.5 - upSpeed / BLEND.apex, 0, 1));
        let pose = mixPose(mixPose(AIR.rise, AIR.leap, running), mixPose(AIR.fall, AIR.leapFall, running), falling);
        this.wallReach = clamp((this.wallReach ?? 0) + (wall && this.flipTime < 0 ? 1 : -1) * ms / BLEND.reach, 0, 1);
        pose = mixPose(pose, AIR.reach, smooth(this.wallReach));
        // Holding down while falling the runner gets ready to roll: knees up, hands out in front for the ground.
        if (this.vertical < 0) pose = mixPose(pose, AIR.roll, falling);
        // A long fast fall flails until the ground is close, then braces for it.
        this.fallTime = upSpeed < -FLAIL.speed ? (this.fallTime ?? 0) + ms : Math.max(0, (this.fallTime ?? 0) - ms * 2);
        let flail = smooth(clamp((this.fallTime - FLAIL.after) / FLAIL.ramp, 0, 1));
        if (flail > 0) {
            const ground = this.probe(hip.x, hip.y, up, FLAIL.probe);
            const toGround = ground ? Math.max(0, ground.distance - standHeight) / Math.max(1, -upSpeed) / dt * ms : Infinity;
            flail *= smooth(clamp((toGround - FLAIL.ground) / FLAIL.ground, 0, 1)) * (1 - this.wallReach);
            this.flailPhase = ((this.flailPhase ?? 0) + ms / FLAIL.period) % 1;
            const wave = offset => (1 + Math.sin((this.flailPhase + offset) * Math.PI * 2)) / 2;
            const arm = offset => [mix(...FLAIL.shoulder, wave(offset)), mix(...FLAIL.elbow, wave(offset + 0.25))];
            const leg = offset => mixPoint(FLAIL.front, FLAIL.back, wave(offset));
            pose = mixPose(pose, {front: leg(0), back: leg(0.5), arms: {left: arm(0), right: arm(FLAIL.skew)}}, flail);
        }
        // Hopping on one leg, it stays under the body.
        if (legs.length === 1) pose = {...pose, front: AIR.hop, back: AIR.hop};
        let tuck = 0;
        if (this.flipTime >= 0) {
            const t = this.flipTime / JUMP.flipTime;
            tuck = smooth(clamp(Math.min(t, 1 - t) / BLEND.tuck, 0, 1));
            pose = mixPose(pose, AIR.tuck, tuck);
        }
        this.poseLegs(legs, torso, pose);
        SIDES.forEach(side => this.driveArm(side, pose.arms[side], 1, tuck > 0 ? TONE.arm : TONE.loose));
        // The head looks where the runner is going, down at the ground falling; turning over, it tucks in.
        if (this.flipTime >= 0) this.drive('head_joint', -s * HEAD.tuck);
        else this.lookAt(mix(HEAD.air, HEAD.fall, falling * (1 - flail)));
    }
    // Feet placed in the torso's frame.
    poseLegs(legs, torso, pose) {
        const s = this.flipped ? -1 : 1;
        const angle = torso.GetAngle();
        const torsoUp = {x: Math.sin(angle), y: -Math.cos(angle)};
        const torsoForward = {x: Math.cos(angle) * s, y: Math.sin(angle) * s};
        const forward = Math.atan2(torsoForward.y, torsoForward.x);
        const hip = this.hipPoint(this.legs[legs[0]]);
        legs.forEach(side => {
            const leg = this.legs[side];
            const target = side === 'left' ? pose.front : pose.back;
            const footAngle = angle + s * 0.15;
            const foot = this.withinReach(leg, {
                x: hip.x + torsoForward.x * target.x - torsoUp.x * target.y,
                y: hip.y + torsoForward.y * target.x - torsoUp.y * target.y,
            }, footAngle);
            this.driveLeg(leg, foot, footAngle, forward);
        });
    }
}

PrefabManager.prefabLibrary.Freerunner = {
    json: PrefabManager.prefabLibrary.NoVehicle.json,
    class: Freerunner,
    library: PrefabManager.LIBRARY_ADMIN,
}

Freerunner.settings = Object.assign({}, BaseVehicle.settings, {
    "selectedVehicle": "Freerunner",
});
Freerunner.settingsOptions = Object.assign({}, BaseVehicle.settingsOptions);
